/*
 * Sneakies — Preorder homepage interactivity (sections/sneakies-preorder-home.liquid)
 * Self-contained, matches the sn-klaviyo.js / sn-storefront.js convention used
 * elsewhere on this site: no build step, no framework, one IIFE per page feature.
 */
(function () {
  /*
   * Two surfaces share this file: the preorder homepage (#sn-po-root) and the
   * PDP (#sn-pdp-root, sections/sneakies-pdp.liquid). The PDP's buy box is a
   * deliberate clone of the homepage's — same `sn-po-*` ids and `data-po-*`
   * hooks — so every module below drives it without modification. The two
   * templates can never render together (index vs product), so reusing the id
   * contract cannot collide.
   *
   * Accepting either root here is what makes that reuse possible: this file is
   * gated on finding one, and each feature below independently bails when its
   * own elements are absent, so PDP-only and homepage-only features coexist.
   */
  var root = document.getElementById('sn-po-root') || document.getElementById('sn-pdp-root');
  if (!root) return;

  /*
   * Run once per page, even if two sections both reference this asset. Without
   * this, a second <script src> for the same file executes again and every
   * handler below is bound twice — which on the buy box would mean two
   * /cart/add submissions from one click. Sections are free to include the tag
   * defensively; the first one wins.
   */
  if (window.__snPreorderInit) return;
  window.__snPreorderInit = true;

  var scriptEl = document.currentScript || document.querySelector('script[src*="sn-preorder.js"]');
  var KLAVIYO_LIST_ID = (scriptEl && scriptEl.getAttribute('data-klaviyo-list-id')) || 'RSPAFx';
  /* The popup subscribes to its own list, separately from the Parents Club
     form, so the two audiences can be segmented apart in Klaviyo. */
  var MYSTERY_LIST_ID = (scriptEl && scriptEl.getAttribute('data-mystery-list-id')) || 'VWMupP';
  var MYSTERY_SOURCE = (scriptEl && scriptEl.getAttribute('data-mystery-source')) || 'email-popup';
  var MYSTERY_DELAY = parseInt((scriptEl && scriptEl.getAttribute('data-mystery-delay')) || '5000', 10);
  var CAPTURE_ENDPOINT = (scriptEl && scriptEl.getAttribute('data-capture-endpoint')) || '';
  /*
   * W2 10% LAUNCH FLAG. Empty unless the section setting
   * w2_10_percent_coupon_enabled is ON, in which case it is "sneaky10".
   * ON: a popup signup is tagged signup_offer = "sneaky10" — the property the
   * Klaviyo W2 flow gates every message on — and does NOT claim the Carrot
   * Cake bag. OFF: behaviour is exactly the Carrot Cake offer. Visitors who
   * claimed Carrot Cake before launch keep it either way: the re-assert paths
   * (carrotClaimed()) read their own localStorage, not this flag.
   */
  var W2_OFFER = (scriptEl && scriptEl.getAttribute('data-w2-offer')) || '';

  /*
   * The Klaviyo profile property a segment should filter on for people who
   * submitted the mystery-discount popup. Klaviyo profiles have no "tags" —
   * tags there attach to lists/segments/flows/campaigns — so profile-level
   * segmentation is done with properties, the same mechanism the existing
   * claim flow uses (free_box, fifty_off, …). The backend writes the SAME
   * property name, so a segment behaves identically whichever path lands.
   */
  var DISCOUNT_PROPERTY = 'discount_email_submitted';
  var DISCOUNT_SOURCE = 'preorder_home_mystery_popup';

  /*
   * FREE CARROT CAKE WELCOME OFFER (replaces the mystery DISCOUNT, 2026-09-03).
   *
   * The offer is now a physical unit, not money off, so the order has to carry
   * something the 3PL can pick from — a changed total would tell them nothing.
   * The carrier is a CART ATTRIBUTE, which Shopify copies onto the order as a
   * note attribute and shows under "Additional details" on the order page.
   *
   * Why an attribute and not a $0 line item: a line item needs a real product,
   * and a $0 Carrot Cake variant sitting in the catalogue is one mis-click away
   * from being orderable on its own. An attribute cannot be bought, cannot be
   * discounted, and cannot change the total.
   *
   * The VALUE is written for a human reading a pick list, and it is worded to
   * settle the edge case that matters: a customer whose pack already contains
   * Carrot Cake still gets one MORE bag. "1 EXTRA bag" and "in addition to pack
   * contents" are load-bearing — a bare "Carrot Cake" would let a packer tick it
   * off against the bag already in the box.
   *
   * The order TAG (for segmentation and for the fulfilment view's filters) is
   * NOT set here and cannot be: the storefront cannot tag an order, and
   * Sneakies-Backend's order webhook holds Klaviyo credentials only, by design.
   * A Shopify Flow keyed on this attribute is what adds the tags — see the
   * ticket notes. This attribute is the single source that Flow reads.
   */
  var CARROT_ATTRIBUTE = 'Free Carrot Cake';
  var CARROT_ATTRIBUTE_VALUE = '1 EXTRA bag — Sneak Peek Fall Flavor, in addition to pack contents';
  var CARROT_CLAIMED_KEY = 'sn_po_carrot_claimed';

  function carrotClaimed() {
    try { return localStorage.getItem(CARROT_CLAIMED_KEY) === '1'; } catch (err) { return false; }
  }

  /*
   * Idempotent: writing the same attribute twice is a no-op on Shopify's side,
   * which is what lets this be called both at claim time and again on later
   * visits. It has to be re-asserted, because the attribute lives on the CART —
   * emptying the cart or arriving in a new session drops it while the claim
   * itself (localStorage) survives.
   *
   * Fire-and-forget on purpose. The visitor is never blocked and never shown an
   * error for this: failing to write it costs a free bag, whereas blocking the
   * popup or the add-to-cart on a cart write costs the order.
   */
  function assertCarrotAttribute() {
    if (!window.fetch) return;
    /** @type {Record<string, string>} */
    var attrs = {};
    attrs[CARROT_ATTRIBUTE] = CARROT_ATTRIBUTE_VALUE;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      }).catch(function () {});
    } catch (err) { /* non-fatal — see above */ }
  }

  function claimCarrotCake() {
    try { localStorage.setItem(CARROT_CLAIMED_KEY, '1'); } catch (err) { /* non-fatal */ }
    assertCarrotAttribute();
  }

  // A claim made on an earlier visit still has to reach THIS cart.
  if (carrotClaimed()) assertCarrotAttribute();

  /*
   * sn-klaviyo.js's subscribeToLaunchList() posts to a hardcoded list ID, so it
   * can't honor this section's klaviyo_list_id setting. Post directly instead,
   * using the same company ID and endpoint shape sn-klaviyo.js uses — this file
   * only ever targets the list configured on THIS section, so it can't affect
   * the pre-launch waitlist sneakies-landing.liquid / sneakies-claim.liquid post to.
   *
   * `properties` is merged by Klaviyo, not replaced, so passing only our own
   * keys here cannot clobber claim-flow properties written elsewhere.
   */
  var KLAVIYO_COMPANY_ID = 'X43p2u';
  function subscribeEmail(email, properties, listIdOverride) {
    if (!email) return;
    var profileAttributes = { email: email };
    if (properties) profileAttributes.properties = properties;
    // The list can be overridden per form, so a section that lives on a
    // different template (the PDP's Parents Club) can target its own list
    // without needing to own the <script> tag that configures this file.
    var listId = listIdOverride || KLAVIYO_LIST_ID;
    try {
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: { profile: { data: { type: 'profile', attributes: profileAttributes } } },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }),
      }).catch(function () {});
    } catch (err) {
      /* fail silently, matching sn-klaviyo.js */
    }
  }

  /*
   * Persists the email to the Shopify `discount_email_capture` metaobject, and
   * re-writes the Klaviyo property server-side as a backstop.
   *
   * Metaobjects are Admin-API-only — the Storefront API can read them but not
   * write them — so this has to go through our own endpoint rather than
   * straight to Shopify. Fire-and-forget: the popup shows its success state
   * regardless, and no Shopify credentials are ever exposed to the browser.
   *
   * Skipped entirely when no endpoint is configured, so the popup keeps working
   * (Klaviyo-only) before the backend is deployed.
   */
  function captureEmail(email) {
    if (!email || !CAPTURE_ENDPOINT) return;
    try {
      fetch(CAPTURE_ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          source: DISCOUNT_SOURCE,
          page_url: window.location && window.location.pathname ? window.location.pathname : '',
        }),
      }).catch(function () {});
    } catch (err) {
      /* fail silently — never block the visitor on our own logging */
    }
  }

  /*
   * All money strings arrive pre-formatted from Liquid's `money` filter (see the
   * sn-po-pricing-data JSON), so this file never formats currency itself — that
   * keeps prices identical to the rest of the store regardless of the shop's
   * money format, currency, or the visitor's locale.
   */

  /* ---------------- Scroll helpers ---------------- */
  root.addEventListener('click', function (e) {
    var scrollBuy = e.target.closest('[data-po-scroll-buy]');
    if (scrollBuy) {
      var buy = document.getElementById('sn-po-buy') || document.getElementById('sn-pdp-buy');
      if (buy) buy.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    var scrollReviews = e.target.closest('[data-po-scroll-reviews]');
    if (scrollReviews) {
      /*
       * On the PDP the reviews live in their own section (sneakies-pdp-reviews),
       * so accept its id too. Falling back rather than branching keeps the
       * homepage path byte-identical in behaviour.
       */
      var reviews = document.getElementById('sn-po-reviews') || document.getElementById('sn-pdp-reviews');
      if (reviews) reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  /* ---------------- Buy-box media gallery ---------------- */
  /*
   * setGalleryPack is this module's one export, consumed by the purchase
   * module's render() below. The three packaging slides carry
   * data-pack-media="8|16|24" (so do their dots and thumbs), and only the
   * selected pack's one is visible at a time — which is what makes the
   * gallery read packaging first, then the four flavour stacks, for every
   * pack. The markup ships with 8 and 24 [hidden] to match the buy box's
   * 16-pack default, so the pre-JS state is already correct.
   */
  var setGalleryPack = null;
  (function initMediaGallery() {
    var stage = document.getElementById('sn-po-media-stage');
    if (!stage) return;
    /*
     * Select by [data-media] rather than stage.children: the PDP places a
     * pre-order callout badge inside the stage, and counting raw children made
     * items.length 9 instead of 8, so wrapping past either end landed on an
     * index no slide owned and the stage went blank. Identical result on the
     * homepage, where every child is a slide.
     */
    var items = Array.prototype.slice.call(stage.querySelectorAll('[data-media]'));
    var dots = Array.prototype.slice.call(document.querySelectorAll('[data-po-media-dot]'));
    var thumbs = Array.prototype.slice.call(document.querySelectorAll('[data-po-media-thumb]'));
    var current = 1;

    /*
     * The slide ids a shopper can reach right now, in slot order. Every wrap
     * and step below works over this list rather than a numeric modulo of
     * items.length — a plain modulo would land on the packaging slides that
     * are [hidden] because their pack is not the selected one.
     */
    function visibleIds() {
      return items.filter(function (el) { return !el.hidden; })
        .map(function (el) { return parseInt(el.dataset.media, 10); });
    }

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var leaveTimer = null;

    var MAX_TILES = 6;

    /*
     * Thumb strip: six tiles in view, sliding so the selected photo's thumb is
     * always one of them. The window starts two before the current thumb
     * (clamped to the ends). While photos remain to the right of the window,
     * its sixth tile becomes a "+N" tile that opens the full-screen viewer.
     * Re-run on every show(): a pack change hides/reveals a packaging thumb.
     */
    var thumbStrip = thumbs.length ? thumbs[0].parentElement : null;
    function layoutThumbs(instant) {
      var vis = thumbs.filter(function (t) { return !t.hidden; });
      thumbs.forEach(function (t) {
        t.classList.remove('is-more', 'is-overflow');
        t.removeAttribute('data-more');
      });
      if (!thumbStrip || !vis.length) return;
      var p = Math.max(0, vis.findIndex(function (t) { return parseInt(t.dataset.poMediaThumb, 10) === current; }));
      var w = 0;
      if (vis.length > MAX_TILES) {
        w = Math.max(0, Math.min(p - 2, vis.length - MAX_TILES));
        var last = w + MAX_TILES - 1;
        if (last < vis.length - 1) {
          vis[last].classList.add('is-more');
          vis[last].setAttribute('data-more', '+' + (vis.length - last));
        }
      }
      var left = vis[w].offsetLeft - vis[0].offsetLeft;
      thumbStrip.scrollTo({ left: left, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
    }

    function show(index, dir) {
      var ids = visibleIds();
      if (!ids.length) return;
      var prevId = current;
      current = ids.indexOf(index) === -1 ? ids[0] : index;
      if (!dir) dir = ids.indexOf(current) < ids.indexOf(prevId) ? -1 : 1;
      var slide = current !== prevId && !reduceMotion;
      items.forEach(function (el) {
        var id = parseInt(el.dataset.media, 10);
        var on = id === current;
        el.classList.remove('is-entering', 'is-leaving');
        if (slide && (on || id === prevId)) {
          // the outgoing photo slides out while the new one pushes in behind it
          el.style.setProperty('--sn-dir', dir < 0 ? '-1' : '1');
          void el.offsetWidth; // restart the animation on rapid clicks
          el.classList.add(on ? 'is-entering' : 'is-leaving');
        }
        el.classList.toggle('is-active', on);
      });
      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(function () {
        items.forEach(function (el) { el.classList.remove('is-leaving', 'is-entering'); });
      }, 480);
      dots.forEach(function (el) {
        el.setAttribute('aria-current', String(parseInt(el.dataset.poMediaDot, 10) === current));
      });
      layoutThumbs();
      thumbs.forEach(function (el) {
        el.setAttribute('aria-current', String(parseInt(el.dataset.poMediaThumb, 10) === current));
      });
      if (viewer && !viewer.root.hidden) viewer.render();
    }

    dots.forEach(function (el) {
      el.addEventListener('click', function () {
        show(parseInt(el.dataset.poMediaDot, 10));
      });
    });
    thumbs.forEach(function (el) {
      el.addEventListener('click', function () {
        var id = parseInt(el.dataset.poMediaThumb, 10);
        if (el.classList.contains('is-more')) {
          openViewer(id);
          return;
        }
        show(id);
      });
    });
    function step(dir) {
      var ids = visibleIds();
      if (!ids.length) return;
      var pos = ids.indexOf(current);
      if (pos === -1) pos = 0;
      show(ids[(pos + dir + ids.length) % ids.length], dir);
    }

    // The photo carries its own hover arrows and the dots row keeps its pair
    // (shown on touch screens), so wire every prev/next control.
    Array.prototype.forEach.call(document.querySelectorAll('[data-po-media-prev]'), function (b) {
      b.addEventListener('click', function (e) { e.stopPropagation(); step(-1); });
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-po-media-next]'), function (b) {
      b.addEventListener('click', function (e) { e.stopPropagation(); step(1); });
    });

    /* Swipe the photo on touch screens. */
    function addSwipe(el, onStep) {
      var x0 = null, y0 = null;
      el.addEventListener('touchstart', function (e) {
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      }, { passive: true });
      el.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          el.snSwiped = true; // swallow the click a swipe produces
          onStep(dx < 0 ? 1 : -1);
        }
      });
    }
    addSwipe(stage, step);

    /*
     * Full-screen photo viewer, opened from the "+N" tile or by clicking the
     * photo. Built here once so the PDP and the homepage share one copy; it
     * drives the same show(), so closing it leaves the gallery on the photo
     * the shopper was looking at.
     */
    var viewer = null;
    function buildViewer() {
      var css = document.createElement('style');
      css.textContent =
        '#sn-gallery-viewer{position:fixed;inset:0;z-index:250;display:flex;align-items:center;justify-content:center;padding:20px}' +
        '#sn-gallery-viewer[hidden]{display:none}' +
        '#sn-gallery-viewer .sgv-backdrop{position:absolute;inset:0;background:rgba(32,21,18,.62)}' +
        '#sn-gallery-viewer .sgv-panel{position:relative;width:min(920px,100%);max-height:94vh;display:flex;flex-direction:column;gap:14px;align-items:center;background:#FFFCEA;border:2.5px solid #201512;border-radius:20px;box-shadow:8px 8px 0 #201512;padding:52px 64px 20px;box-sizing:border-box;animation:sgv-in .22s ease-out both}' +
        '@keyframes sgv-in{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:none}}' +
        '#sn-gallery-viewer .sgv-stage{position:relative;width:100%;max-width:640px;aspect-ratio:1/1;max-height:66vh;overflow:hidden;border-radius:14px;background:#FFFDF4}' +
        '#sn-gallery-viewer .sgv-img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain}' +
        '#sn-gallery-viewer .sgv-img.is-in{animation:sgv-slide .35s cubic-bezier(.22,.61,.36,1) both}' +
        '@keyframes sgv-slide{from{transform:translateX(calc(var(--sn-dir,1) * 40%));opacity:0}to{transform:none;opacity:1}}' +
        '#sn-gallery-viewer button{font:inherit;cursor:pointer}' +
        '#sn-gallery-viewer .sgv-close{position:absolute;top:12px;right:14px;width:38px;height:38px;border-radius:50%;border:1.5px solid rgba(32,21,18,.35);background:#FFFDF4;color:#201512;font-size:22px;line-height:1}' +
        '#sn-gallery-viewer .sgv-arrow{position:absolute;top:calc(50% - 40px);width:44px;height:44px;border-radius:50%;border:1.5px solid rgba(32,21,18,.35);background:#FFFDF4;color:#201512;font-size:18px;box-shadow:2px 2px 0 rgba(32,21,18,.35)}' +
        '#sn-gallery-viewer .sgv-prev{left:12px}#sn-gallery-viewer .sgv-next{right:12px}' +
        '#sn-gallery-viewer .sgv-thumbs{display:flex;gap:8px;max-width:100%;overflow-x:auto;padding:2px 2px 6px;scrollbar-width:none}' +
        '#sn-gallery-viewer .sgv-thumbs::-webkit-scrollbar{display:none}' +
        '#sn-gallery-viewer .sgv-thumbs button{flex:none;width:64px;height:64px;padding:0;border-radius:8px;overflow:hidden;border:1.5px solid rgba(32,21,18,.22);background:#FFFCEA;transition:border-color .2s ease}' +
        '#sn-gallery-viewer .sgv-thumbs button[aria-current="true"]{border:2.5px solid #201512}' +
        '#sn-gallery-viewer .sgv-thumbs img{width:100%;height:100%;object-fit:cover}' +
        '@media (max-width:699px){#sn-gallery-viewer{padding:0}#sn-gallery-viewer .sgv-panel{border-radius:0;border:0;box-shadow:none;height:100%;max-height:none;justify-content:center;padding:56px 12px 16px}#sn-gallery-viewer .sgv-arrow{display:none}}' +
        '@media (prefers-reduced-motion:reduce){#sn-gallery-viewer .sgv-panel,#sn-gallery-viewer .sgv-img.is-in{animation:none}}';
      document.head.appendChild(css);

      var root = document.createElement('div');
      root.id = 'sn-gallery-viewer';
      root.hidden = true;
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('aria-label', 'Product photos');
      root.innerHTML =
        '<div class="sgv-backdrop" data-sgv-close></div>' +
        '<div class="sgv-panel">' +
          '<button type="button" class="sgv-close" aria-label="Close photos" data-sgv-close>&times;</button>' +
          '<button type="button" class="sgv-arrow sgv-prev" aria-label="Previous photo">&lsaquo;</button>' +
          '<div class="sgv-stage"><img class="sgv-img" alt=""></div>' +
          '<button type="button" class="sgv-arrow sgv-next" aria-label="Next photo">&rsaquo;</button>' +
          '<div class="sgv-thumbs" role="group" aria-label="All photos"></div>' +
        '</div>';
      document.body.appendChild(root);

      var img = root.querySelector('.sgv-img');
      var strip = root.querySelector('.sgv-thumbs');
      var lastFocus = null;
      var shownId = null;

      function srcOf(el) { return el.currentSrc || el.src; }
      function render() {
        var ids = visibleIds();
        var el = items.filter(function (i) { return parseInt(i.dataset.media, 10) === current; })[0];
        if (!el) return;
        if (shownId !== current) {
          var dir = shownId === null ? 0 : (ids.indexOf(current) < ids.indexOf(shownId) ? -1 : 1);
          img.src = srcOf(el);
          img.alt = el.alt || '';
          img.classList.remove('is-in');
          if (dir && !reduceMotion) {
            img.style.setProperty('--sn-dir', String(dir));
            void img.offsetWidth;
            img.classList.add('is-in');
          }
          shownId = current;
        }
        strip.innerHTML = '';
        ids.forEach(function (id) {
          var it = items.filter(function (i) { return parseInt(i.dataset.media, 10) === id; })[0];
          var b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', 'Show ' + (it.alt || 'photo'));
          b.setAttribute('aria-current', String(id === current));
          var t = document.createElement('img');
          t.src = srcOf(it); t.alt = ''; t.loading = 'lazy';
          b.appendChild(t);
          b.addEventListener('click', function () { show(id); });
          strip.appendChild(b);
          if (id === current) setTimeout(function () { b.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }, 0);
        });
      }
      function close() {
        root.hidden = true;
        document.documentElement.style.overflow = '';
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
      root.addEventListener('click', function (e) {
        if (e.target.closest('[data-sgv-close]')) close();
      });
      root.querySelector('.sgv-prev').addEventListener('click', function () { step(-1); });
      root.querySelector('.sgv-next').addEventListener('click', function () { step(1); });
      root.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      });
      addSwipe(root.querySelector('.sgv-stage'), step);
      return {
        root: root,
        render: render,
        open: function () {
          lastFocus = document.activeElement;
          shownId = null;
          root.hidden = false;
          document.documentElement.style.overflow = 'hidden';
          render();
          root.querySelector('.sgv-close').focus();
        }
      };
    }
    function openViewer(id) {
      if (!viewer) viewer = buildViewer();
      if (id && id !== current) show(id);
      viewer.open();
    }
    // Click the photo to open it full screen (the hover arrows stop their own clicks).
    stage.addEventListener('click', function (e) {
      if (e.target.closest('[data-po-media-prev],[data-po-media-next]')) return;
      if (stage.snSwiped) { stage.snSwiped = false; return; }
      openViewer(current);
    });

    /*
     * Arrow-key support for the thumbnail strip.
     *
     * The reference design has this too, but its handler wraps with `% 5` while
     * rendering 8 thumbnails — so keyboard users could never reach slots 6-8.
     * Wrapping over the VISIBLE thumbs fixes that and also skips the pack
     * thumbs hidden right now; show() already normalises the index, so this
     * only needs to move focus to match the new selection.
     */
    thumbs.forEach(function (el) {
      el.addEventListener('keydown', function (e) {
        var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        var vis = thumbs.filter(function (t) { return !t.hidden && !t.classList.contains('is-overflow'); });
        var pos = vis.indexOf(el);
        if (pos === -1) return;
        var target = vis[(pos + dir + vis.length) % vis.length];
        show(parseInt(target.dataset.poMediaThumb, 10));
        target.focus();
      });
    });

    /*
     * Pack size <-> gallery. Called from the purchase module's render() on
     * every state change and cheap to call redundantly: it bails unless the
     * pack actually changed, so toggling Subscribe & Save or a frequency
     * never yanks the carousel anywhere. On a real pack change it reveals
     * that pack's packaging slide/dot/thumb, hides the other packs', and
     * jumps to the packaging shot — the shopper just chose a box, so show
     * them that box. '16' matches the [hidden] defaults the sections render.
     */
    layoutThumbs(true);

    var galleryPackKey = '16';
    setGalleryPack = function (key) {
      key = String(key || '');
      if (!key || key === galleryPackKey) return;
      galleryPackKey = key;
      [items, dots, thumbs].forEach(function (list) {
        list.forEach(function (el) {
          if (!el.dataset.packMedia) return;
          el.hidden = el.dataset.packMedia !== key;
        });
      });
      var packSlide = items.filter(function (el) { return el.dataset.packMedia === key; })[0];
      show(packSlide ? parseInt(packSlide.dataset.media, 10) : current);
    };
  })();

  /* ----------------------------------------------------------------------
   * Purchase module: pack size / Subscribe & Save vs one-time
   *
   * Pack size is 3 SEPARATE products, each with ONE variant at the one-time
   * price. Subscribe & Save is a REAL selling plan group on all three
   * (rebuilt 2026-08-18 — BACKEND_PENDING item 9 closed): subscribing
   * submits the variant id plus a `selling_plan` id, which is what makes
   * checkout create a recurring subscription contract instead of a one-off
   * order. The frequency pills match plans from the pricing island by their
   * LABEL (delivery interval parsed from optionValue/name — array order is
   * not a contract; index is only the last-resort fallback when no label
   * parses), and the hidden selling_plan input is DISABLED for one-time
   * purchases so it never submits. If a pack arrives with no plans (deleted in the Subscriptions
   * app), the subscribe option is withdrawn entirely rather than selling a
   * subscription that cannot renew — same honesty rule as everywhere else.
   * Pricing/display contract: snippets/sn-po-pricing-island.liquid.
   * ---------------------------------------------------------------------- */
  (function initPurchaseModule() {
    var dataEl = document.getElementById('sn-po-pricing-data');
    var form = document.getElementById('sn-po-form');
    if (!dataEl || !form) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (err) {
      return;
    }

    var variantIdInput = form.querySelector('[ref="variantId"]');
    var planInput = document.getElementById('sn-po-plan-input');

    /*
     * Plan matching. Pills carry data-freq-value ("Every month" / "Every two
     * months"); island plans carry optionValue/label ("Deliver every month" /
     * "Deliver every 2 months" — strings the subscription app controls). Both
     * reduce to a delivery interval in months, so the match survives either
     * side rewording, plan reordering, and the app migration. selectedPlanIdx
     * remains ONLY as the fallback when no label parses at all — that path is
     * today's exact by-index behavior.
     */
    function planInterval(label) {
      var m = String(label || '').toLowerCase().match(/every\s*(\d+|two|three)?\s*-?\s*month/);
      if (!m) return null;
      if (!m[1]) return 1;
      if (m[1] === 'two') return 2;
      if (m[1] === 'three') return 3;
      return parseInt(m[1], 10) || null;
    }
    function findPlan(plans, interval) {
      if (interval == null) return null;
      for (var i = 0; i < plans.length; i++) {
        if (planInterval(plans[i].optionValue || plans[i].label) === interval) return plans[i];
      }
      return null;
    }
    var selectedPlanIdx = 0; // fallback only — see planInterval above
    var selectedFreqLabel = (function () {
      var checked = document.querySelector('[data-po-freq][aria-checked="true"]');
      return checked ? (checked.getAttribute('data-freq-value') || checked.textContent) : 'Every month';
    })();
    var packButtons = Array.prototype.slice.call(document.querySelectorAll('[data-po-pack]'));
    var packs = data.packs || [];
    var isSubMode = true; // Subscribe & Save is the default selection to match the approved design
    // True while an /cart/add submission is in flight. render() leaves the CTA
    // alone while set — the instant-drawer submit handler below owns it then.
    var addingToCart = false;

    /*
     * `?plan=sub` / `?plan=once` deep link.
     *
     * The announcement bar links here expecting it to work
     * (/?plan=sub#sn-po-buy, sections/header-group.json). It did nothing before
     * — nothing read the parameter, so it just landed on the default selection
     * and looked right by accident on the subscribe path.
     *
     * The footer used to carry the same param at the PDP (?plan=sub#sn-pdp-buy),
     * where it was always inert — assets/sn-pdp.js reads no query params — so
     * that link now points at the account's subscription view instead.
     *
     * The hash is left to the browser, which scrolls to #sn-po-buy / #sn-pdp-buy
     * natively; this only sets which plan card opens.
     */
    try {
      var planParam = new URLSearchParams(window.location.search).get('plan');
      if (planParam === 'sub') isSubMode = true;
      else if (planParam === 'once' || planParam === 'onetime') isSubMode = false;
    } catch (err) {
      /* URLSearchParams is everywhere we support; a failure here must not stop
         the buy box from rendering at its default. */
    }
    var initialPack = packs.filter(function (p) { return p.key === '16'; })[0] || packs[0];
    var selectedPack = initialPack;

    function setText(sel, text) {
      var el = document.querySelector(sel);
      if (el) el.textContent = text;
    }

    /* ------------------------------------------------------------------
     * THE SHIPPING MATRIX — one function, and every shipping claim in the
     * buy box is derived from it. Nothing else in this file decides whether
     * shipping is free.
     *
     *                 8-pack   16-pack   24-pack
     *   one-time       paid      FREE      FREE
     *   subscribe      paid      FREE      FREE
     *   (renewals follow the subscribe row)
     *
     * Free shipping is a property of the PACK, not the purchase type: on the
     * store it is a Shopify delivery-profile split keyed on the variant — the
     * 16- and 24-Bag variants sit in "Sneakies 16/24 packs — free US shipping",
     * the 8-Bag variant in the $9.99 standard profile — so a one-time 16-pack
     * and a subscribed 16-pack both check out free. Earlier this function also
     * took `subscribed` and only returned free for subscriptions, which made
     * the buy box UNDER-promise: a one-time 16-pack showed paid shipping and
     * was then charged $0. `pack.freeShipping` (published per pack by the
     * pricing island: 16/24 true, 8 false) is now the whole answer, matching
     * the delivery profiles exactly. Do NOT reintroduce a purchase-type term —
     * free shipping must not read as a subscription perk (8-pack stays paid in
     * both columns unless Arun approves otherwise).
     * ------------------------------------------------------------------ */
    function shipsFree(pack) {
      return !!(pack && pack.freeShipping);
    }

    /*
     * Rebuilds every shipping-dependent string in the buy box from shipsFree():
     * both benefit lists and the pack-pill badges. Called from render(), so it
     * re-runs on every pack change AND every purchase-type change — the two
     * things the matrix reads.
     */
    function renderBenefits() {
      var b = data.benefits;
      if (!b || !selectedPack) return;

      /*
       * NEITHER column prints a PAID shipping line any more: the
       * "Standard shipping $9.99" row was removed from both cards by request.
       * The only shipping line either card can show is the free one, and it
       * appears in BOTH columns for a pack that ships free (16/24) — because
       * the delivery-profile split is per-variant, so a one-time 16-pack is
       * just as free as a subscribed one. shipsFree() is still the single
       * source of truth, so nothing here can drift from the matrix, and
       * b.freeShipping is guarded the same way the other optional lines are:
       * an island that predates it should drop the row, not print "undefined".
       */
      var packShipsFree = shipsFree(selectedPack) && !!b.freeShipping;

      var subList = [b.subBase];
      if (b.extraOff) subList.push(b.extraOff);
      // Sneak Peek bag is the second benefit, above free shipping (Rohit, 2026-11-02).
      subList.push(b.flavorLab);
      if (packShipsFree) subList.push(b.freeShipping);
      subList.push(b.cancelAnytime, b.guarantee);

      var onceList = [];
      if (b.onceExtraOff) onceList.push(b.onceExtraOff);
      if (packShipsFree) onceList.push(b.freeShipping);
      onceList.push(b.guarantee);

      fill('[data-po-benefits="sub"]', subList);
      fill('[data-po-benefits="once"]', onceList);

      /*
       * The pack pills carry the same claim as a badge. Each badge shows
       * exactly when its OWN pack ships free (16/24), independent of which
       * pack is selected and of the purchase type — the badge is a truthful
       * size-up cue, and a one-time 16-pack really does ship free. The badge
       * is aria-hidden decoration; the benefit list above is the accessible
       * claim, so toggling `hidden` is the whole job.
       */
      var pills = document.querySelectorAll('[data-po-pack]');
      for (var i = 0; i < pills.length; i++) {
        var badge = pills[i].querySelector('.po-pack-badge, .pd-pack-badge');
        if (!badge) continue;
        var pillPack = packByKey(pills[i].getAttribute('data-pack-key'));
        badge.hidden = !shipsFree(pillPack);
      }
    }

    function packByKey(key) {
      for (var i = 0; i < packs.length; i++) {
        if (packs[i].key === key) return packs[i];
      }
      return null;
    }

    function fill(sel, lines) {
      var host = document.querySelector(sel);
      if (!host) return;
      // textContent per <span> — never innerHTML, since these strings are
      // merchant-editable settings and must not be able to inject markup.
      host.textContent = '';
      lines.forEach(function (line) {
        if (!line) return;
        var span = document.createElement('span');
        span.textContent = line;
        host.appendChild(span);
      });
    }

    function render() {
      if (!selectedPack) return;
      /*
       * A subscription can only be offered while real selling plans exist on
       * the pack. plans comes from the variant's live selling_plan_allocations,
       * so deleting the plans in the Subscriptions app withdraws the subscribe
       * option here on the next render — nothing is sold that cannot renew.
       */
      var subPlans = (selectedPack.sub && selectedPack.sub.plans) || [];
      var subPossible = subPlans.length > 0 && selectedPack.sub.available !== false;
      if (isSubMode && !subPossible) isSubMode = false;
      var mode = isSubMode ? selectedPack.sub : selectedPack.once;
      variantIdInput.value = (mode && mode.id) || '';
      if (planInput) {
        // Disabled inputs never serialize, so one-time submissions carry no
        // selling_plan field at all — not an empty one, which /cart/add rejects.
        var plan = findPlan(subPlans, planInterval(selectedFreqLabel)) ||
                   subPlans[selectedPlanIdx] || subPlans[0] || null;
        if (isSubMode && plan) { planInput.disabled = false; planInput.value = String(plan.id); }
        else { planInput.disabled = true; planInput.value = ''; }
      }

      packButtons.forEach(function (btn) {
        btn.setAttribute('aria-checked', String(btn.dataset.packKey === selectedPack.key));
      });

      // Keep the gallery's packaging slide in step with the selected pack.
      if (setGalleryPack) setGalleryPack(selectedPack.key);

      var sub = selectedPack.sub;
      var once = selectedPack.once;

      /*
       * Prices are the DUE-TODAY figures — variant price less the pre-order
       * discount — because that is what the design shows and what the shopper is
       * charged (the $5 is a live automatic discount, applied by Shopify at cart).
       * Liquid computes and formats them; see the `dueFmt` keys in the island.
       *
       * `|| priceFmt` throughout: an island written before these keys existed
       * still renders, just at plain variant prices.
       */
      var subDue = sub.due != null ? sub.due : sub.price;
      var onceDue = once.due != null ? once.due : once.price;
      var anchor = selectedPack.anchorFmt || once.priceFmt;

      // Subscribe row: anchor against the FULL one-time price, so the strikethrough
      // shows the combined saving (cheaper plan + pre-order discount).
      var subHasSaving = once.price > subDue;
      setText('[data-po-price="sub"]', sub.dueFmt || sub.priceFmt);
      setText('[data-po-compare="sub"]', subHasSaving ? anchor : '');
      setText('[data-po-save="sub"]', subHasSaving ? 'Save ' + selectedPack.subSaveFmt : '');

      /*
       * One-time row: struck through whenever the price shown is below the variant
       * price. Previously this only fired when a compare-at price was set, and none
       * is on these variants — so the pre-order saving was invisible on this card
       * even while the design showed $65 against $70.
       */
      var onceHasSaving = onceDue < once.price ||
                          (once.compareAt != null && once.compareAt > once.price);
      setText('[data-po-price="once"]', once.dueFmt || once.priceFmt);
      setText('[data-po-compare="once"]', onceHasSaving ? anchor : '');
      setText('[data-po-save="once"]', onceHasSaving ? 'Save ' + selectedPack.onceSaveFmt : '');

      var expandedSub = document.getElementById('sn-po-option-sub');
      var expandedOnce = document.getElementById('sn-po-option-once');
      var compactSub = document.getElementById('sn-po-option-sub-compact');
      var compactOnce = document.getElementById('sn-po-option-once-compact');

      if (expandedSub) { expandedSub.hidden = !isSubMode; expandedSub.setAttribute('aria-checked', String(isSubMode)); }
      if (expandedOnce) { expandedOnce.hidden = isSubMode; expandedOnce.setAttribute('aria-checked', String(!isSubMode)); }
      if (compactOnce) {
        compactOnce.hidden = !isSubMode;
        var compactOnceDue = compactOnce.querySelector('[data-po-compact-due]');
        if (compactOnceDue) compactOnceDue.textContent = once.dueFmt || once.priceFmt;
        var compactOnceWas = compactOnce.querySelector('[data-po-compact-was]');
        if (compactOnceWas) compactOnceWas.textContent = onceHasSaving ? anchor : '';
      }
      if (compactSub) {
        // Its savings sentence is static copy, server-rendered by Liquid.
        compactSub.hidden = isSubMode || !subPossible;
      }

      renderBenefits();

      var cta = document.getElementById('sn-po-cta');
      if (cta && !addingToCart) {
        // Skipped entirely while an add is in flight — the submit handler
        // below owns the button then ("Adding…", disabled), and a re-render
        // mid-flight (a pack click racing the network) must not re-enable a
        // button whose submission is still pending.
        //
        // Reads "Add to cart" whenever the variant can actually be bought.
        // "Sold out" is kept for the genuinely-unavailable case: showing
        // "Add to cart" on a disabled button, or letting a click fail at
        // /cart/add, is worse than saying why it can't be added.
        var isUnavailable = !mode || mode.available === false || !mode.id;
        cta.disabled = isUnavailable;
        // The design uses one label on both surfaces — "Add to cart", lowercase c
        // (ctaLabelText in the source; "Add to Cart" appears nowhere in it). An
        // earlier per-surface data attribute here was built on a misreading and
        // has been removed.
        cta.textContent = isUnavailable ? 'Sold out' : 'Add to cart';
      }

      // Frequency only applies to a subscription selection.
      var freqGroup = document.getElementById('sn-po-freq-group');
      if (freqGroup) freqGroup.hidden = !isSubMode || !subPossible;

      var qtyEls = document.querySelectorAll('[data-po-inside-qty]');
      qtyEls.forEach(function (el) { el.textContent = String(selectedPack.flavorQty); });
    }

    packButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var match = packs.filter(function (p) { return p.key === btn.dataset.packKey; })[0];
        if (match) { selectedPack = match; render(); }
      });
    });

    /*
     * Purchase type (Subscribe & Save vs One-time).
     *
     * Two accessibility constraints shape this, both found in keyboard testing:
     *
     *   1. The expanded option is a <div role="radio">, not a <button>, because
     *      it contains block content a <button> may not legally hold. A div does
     *      not synthesise a click from Space/Enter the way a button does, so it
     *      was focusable but dead to the keyboard. selectPurchase() is therefore
     *      bound to keydown as well as click.
     *
     *   2. Choosing an option HIDES the element that was just activated (the
     *      compact row becomes the expanded card), which dropped focus to
     *      <body> and threw keyboard users back to the top of the document.
     *      After render() we move focus to the option that is now expanded —
     *      the same control, in its new state.
     */
    function selectPurchase(sub) {
      isSubMode = sub;
      render();
      var expanded = document.getElementById(sub ? 'sn-po-option-sub' : 'sn-po-option-once');
      if (expanded && !expanded.hidden) expanded.focus();
    }

    function bindPurchase(ids, sub) {
      ids.forEach(function (id) {
        var el = document.getElementById(id);
        if (!el) return;
        el.addEventListener('click', function () { selectPurchase(sub); });
        // Only the div needs this; on the compact <button> the browser already
        // fires click for Space/Enter and a second handler would double-fire.
        if (el.tagName === 'DIV') {
          el.addEventListener('keydown', function (e) {
            if (e.key !== ' ' && e.key !== 'Spacebar' && e.key !== 'Enter') return;
            e.preventDefault();
            selectPurchase(sub);
          });
        }
      });
    }

    bindPurchase(['sn-po-option-sub', 'sn-po-option-sub-compact'], true);
    bindPurchase(['sn-po-option-once', 'sn-po-option-once-compact'], false);

    /*
     * Frequency pills select a real selling plan, matched by the pill's
     * data-freq-value against the island plans' optionValue/label (interval
     * parse — see planInterval above). selectedPlanIdx is kept in step only
     * as the no-label-parses fallback; render() writes the resolved plan id
     * into the selling_plan input.
     */
    var freqButtons = Array.prototype.slice.call(document.querySelectorAll('[data-po-freq]'));
    freqButtons.forEach(function (btn, idx) {
      btn.addEventListener('click', function () {
        freqButtons.forEach(function (b) {
          b.setAttribute('aria-checked', String(b === btn));
        });
        selectedPlanIdx = idx;
        selectedFreqLabel = btn.getAttribute('data-freq-value') || btn.textContent;
        render();
      });
    });

    /*
     * Instant cart drawer.
     *
     * The stock ProductFormComponent opens the drawer only AFTER the
     * /cart/add.js round trip resolves (it dispatches cart:update, and the
     * drawer's auto-open listens for that). Between click and open the page
     * gave no feedback at all: the button is not wrapped in
     * <add-to-cart-component>, so none of the theme's added/animating states
     * ever ran, and nothing guarded a second click from queueing a second add.
     *
     * This listener runs on the same submit the stock component handles (it
     * does not preventDefault — the stock code still owns the network) and
     * does three things immediately:
     *   1. disables the CTA and relabels it "Adding…" — which is also the
     *      double-submit guard the stock component lacks here;
     *   2. opens the drawer NOW, veiled by the .sn-cd-adding overlay
     *      (sn-cart-drawer.css) — the drawer's initial Liquid is the
     *      page-load cart, which for a first add says "Your cart is empty",
     *      so the veil is load-bearing, not decoration;
     *   3. arms a fallback: the stock component swallows add failures when no
     *      error ref exists, so if no cart:update arrives the veil lifts, the
     *      button restores, and the error line under the CTA speaks.
     *
     * cart:update ends the state: the same event that morphs the fresh cart
     * into the drawer lifts the veil, so content and reveal land together.
     */
    var addForm = document.getElementById('sn-po-product-form');
    var ADD_TIMEOUT_MS = 10000;
    var addTimer = null;

    function drawerEl() { return document.querySelector('cart-drawer-component'); }

    function endAdding(failed) {
      if (!addingToCart) return;
      addingToCart = false;
      if (addTimer) { clearTimeout(addTimer); addTimer = null; }
      var drawer = drawerEl();
      if (drawer) drawer.classList.remove('sn-cd-adding');
      render(); // restores the CTA to whatever state the buy box is really in
      if (failed) {
        // The stock component toggles the 'hidden' CLASS on this element (its
        // own error path does classList.remove('hidden')), so this does too.
        var err = document.querySelector('[ref="addToCartTextError"]');
        if (err) {
          if (!err.textContent) err.textContent = 'Could not add to cart. Please try again.';
          err.classList.remove('hidden');
        }
        // A failed add leaves the drawer showing the stale pre-add cart;
        // close it rather than present old contents as the result.
        var dlg = drawer && drawer.querySelector('dialog');
        if (dlg && dlg.open && typeof drawer.closeDialog === 'function') drawer.closeDialog();
      }
    }

    if (addForm && window.fetch) {
      addForm.addEventListener('submit', function () {
        if (addingToCart) return;
        /*
         * Re-assert the welcome-offer attribute on the way into the cart. The
         * page-load assert above covers the common case, but a visitor who
         * claimed the offer and then emptied their cart would otherwise reach
         * checkout with no attribute and no free bag. Fire-and-forget, so it
         * never races or delays the add the stock component is about to make.
         */
        if (carrotClaimed()) assertCarrotAttribute();
        var drawer = drawerEl();
        addingToCart = true;
        var err = document.querySelector('[ref="addToCartTextError"]');
        if (err) { err.classList.add('hidden'); err.textContent = ''; }
        var ctaNow = document.getElementById('sn-po-cta');
        if (ctaNow) { ctaNow.disabled = true; ctaNow.textContent = 'Adding…'; }
        if (drawer && typeof drawer.showDialog === 'function') {
          drawer.classList.add('sn-cd-adding');
          drawer.showDialog();
        }
        addTimer = setTimeout(function () { endAdding(true); }, ADD_TIMEOUT_MS);
      });

      // The same event the drawer auto-opens and re-renders on. By the time it
      // fires, morphSection has already swapped the fresh cart in.
      document.addEventListener('cart:update', function () { endAdding(false); });
      // The stock component announces add failures (and writes its own message
      // into the error element) — end the veil right away instead of letting
      // the 10s catch-all do it.
      document.addEventListener('cart:error', function () { endAdding(true); });
    }

    render();
  })();

  /* ---------------- Nutrition lightbox ---------------- */
  (function initNutrition() {
    var modal = document.getElementById('sn-po-nutrition');
    if (!modal) return;

    /*
     * The four panels sit on one line, ~230px each, which is enough to tell the
     * flavours apart and nowhere near enough to read a nutrition table — so each
     * one is a button that opens the full image over the modal. The row is
     * navigation; this is where the information is.
     *
     * The enlarged image reuses the row image's own src, so it costs no extra
     * request: the browser already has the full 1100px file, the row was just
     * painting it small.
     */
    var zoom = modal.querySelector('[id$="-nfp-zoom"]');
    var zoomImg = zoom && zoom.querySelector('[data-po-nfp-zoom-img], [data-pd-nfp-zoom-img]');
    var zoomOpener = null;

    function closeZoom() {
      if (!zoom || zoom.hidden) return;
      zoom.hidden = true;
      if (zoomImg) zoomImg.removeAttribute('src');
      if (zoomOpener && typeof zoomOpener.focus === 'function') zoomOpener.focus();
      zoomOpener = null;
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-po-nutrition-open]')) { modal.hidden = false; }
      if (e.target.closest('[data-po-nutrition-close]')) { closeZoom(); modal.hidden = true; }

      if (!zoom || !zoomImg) return;
      var opener = e.target.closest('[data-po-nfp-zoom], [data-pd-nfp-zoom]');
      if (opener) {
        var full = opener.querySelector('img');
        if (!full) return;
        zoomImg.src = full.currentSrc || full.src;
        zoomImg.alt = full.alt || '';
        zoom.hidden = false;
        zoomOpener = opener;
        // button[...], not [...]: the SCRIM carries the same close attribute and
        // comes first in the DOM, so a bare attribute selector returns the div
        // and focus goes nowhere.
        var closeBtn = zoom.querySelector('button[data-po-nfp-zoom-close], button[data-pd-nfp-zoom-close]');
        if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
        return;
      }
      if (e.target.closest('[data-po-nfp-zoom-close], [data-pd-nfp-zoom-close]')) closeZoom();
    });

    /*
     * One Escape handler, with explicit precedence: the enlarged panel is on
     * top, so it closes first and the modal stays open. A second listener would
     * peel both in one press — the exact defect PR #12 fixed for the reviews
     * sort menu, where "an open menu now owns Escape".
     */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || modal.hidden) return;
      if (zoom && !zoom.hidden) { closeZoom(); return; }
      modal.hidden = true;
    });
  })();

  /* ---------------- Overlay scroll lock ---------------- */
  /*
   * The page behind an open overlay must not scroll — on a phone the mystery
   * popup's scrim covers the whole viewport, so a scrollable page underneath
   * reads as a broken page. Watches the `hidden` attribute (the one signal
   * every overlay here already toggles) and saves/restores the previous
   * inline overflow, so stacking on top of the all-reviews dialog (which sets
   * body overflow itself) unwinds correctly.
   *
   * The nutrition dialog is EXCLUDED on the PDP: sn-pdp.js already locks it
   * there (with a focus trap on top), and two watchers on one attribute would
   * fight over the saved value.
   */
  (function initOverlayScrollLock() {
    if (typeof MutationObserver !== 'function') return;
    var ids = ['sn-po-mystery'];
    if (!document.getElementById('sn-pdp-root')) ids.push('sn-po-nutrition');
    ids.forEach(function (id) {
      var modal = document.getElementById(id);
      if (!modal) return;
      var previousOverflow = '';
      var wasHidden = modal.hidden;
      if (!wasHidden) {
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }
      new MutationObserver(function () {
        if (modal.hidden === wasHidden) return;
        wasHidden = modal.hidden;
        if (modal.hidden) {
          document.body.style.overflow = previousOverflow;
        } else {
          previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
        }
      }).observe(modal, { attributes: true, attributeFilter: ['hidden'] });
    });
  })();

  /* ======================================================================
   * REVIEWS — real Okendo data, Sneakies-designed cards.
   *
   * Okendo's own widget is not rendered on this site. Its markup could not be
   * made to look like the approved design without fighting app-versioned CSS,
   * so the cards are ours and only the DATA is Okendo's, pulled from the same
   * public, store-scoped endpoint its widget calls from the browser:
   *
   *   GET https://api.okendo.io/v1/stores/{sid}/products/shopify-{pid}/reviews
   *       ?limit=100&orderBy=rating desc      (then follow `nextUrl`)
   *
   * Product-scoped, asking for everything. `limit` caps a PAGE at 100, so the
   * response carries a `nextUrl` whenever more exist and every page is
   * followed — see load(). Both surfaces — the carousel and the All-reviews
   * dialog — share that payload, and each decides for itself how much of it to
   * show. Fetch size is deliberately not a per-surface choice any more: it
   * used to be, and the dialog inherited the carousel's 12.
   *
   * Product-scoped rather than store-wide because the star badge beside the buy
   * box is server-rendered from this product's own okendo.summaryData
   * metafield. Okendo's Product Group makes that a group figure, while the
   * store-wide list also carries site reviews that belong to no product (55).
   * Asking per product keeps every number on the page the same number.
   *
   * The summary panel's total, average and distribution are computed from the
   * payload rather than read from /review_aggregate, which is store-wide and
   * would disagree with the list it sits above.
   *
   * Unauthenticated and CORS-open — verified by fetching from a 127.0.0.1
   * preview and again against the live store. The subscriber id comes from the
   * shop metafield okendo.SubscriberId, printed onto the band by Liquid, so it
   * is never hardcoded here.
   *
   * If the fetch fails, nothing is invented: the band keeps its heading and the
   * server-rendered aggregate badge, and the empty carousel is removed. That is
   * the whole failure policy — a review section that shows less is fine, one
   * that shows fiction is not.
   * ==================================================================== */
  var snReviews = (function () {
    var API = 'https://api.okendo.io/v1/stores/';
    var API_ROOT = 'https://api.okendo.io/v1';   // nextUrl is relative to this
    var CACHE_TTL_MS = 60 * 60 * 1000;   // an hour; reviews do not move fast
    var PAGE_SIZE = 100;                  // the endpoint's per-request ceiling
    var MAX_PAGES = 20;                   // 2000 reviews; a stop, not a target
    /*
     * In-flight promises keyed by scope. This was a single variable, and that
     * was the bug: the carousel asked for 12 reviews first, the dialog asked
     * for 100 later and was handed the carousel's 12 because a promise already
     * existed. Keying it means a caller can only ever receive the payload it
     * asked for.
     */
    var pending = {};

    /*
     * Okendo auto-generates a title of "5 Stars" (or "4 Stars", …) for reviews
     * submitted without one — some of this store's reviews have one. Printing
     * it gives every card an identical, meaningless headline that also restates
     * the star row directly above it, so it is dropped and the card renders with
     * no headline at all. Human titles ("Loved it!", "Awesome") are kept.
     */
    function realTitle(title) {
      if (!title) return '';
      var t = String(title).trim();
      if (/^\d+\s*stars?$/i.test(t)) return '';
      if (/^rated\s+\d+(\.\d+)?\s*(out of\s*5)?\s*stars?$/i.test(t)) return '';
      return t;
    }

    function normalize(raw) {
      var list = (raw && raw.reviews) || [];
      return list.map(function (r) {
        var reviewer = r.reviewer || {};
        var media = [];
        if (r.media && r.media.length) {
          /*
           * A still for every media item, including video.
           *
           * Video items carry no largeUrl, so the old chain fell through to
           * fullSizeUrl — which for a video is the .mp4 itself, and both card
           * builders assign this straight to img.src. One review (a video) has
           * been rendering an empty bordered pane on the live site because of
           * it. Okendo gives videos a poster frame; use that.
           */
          media = r.media
            .map(function (m) {
              if (!m) return null;
              return m.type === 'video'
                ? (m.videoPosterUrl || m.thumbnailUrl)
                : (m.largeUrl || m.fullSizeUrl || m.thumbnailUrl);
            })
            .filter(Boolean);
        }
        return {
          id: r.reviewId,
          rating: Number(r.rating) || 0,
          title: realTitle(r.title),
          body: (r.body || '').trim(),
          name: (reviewer.displayName || '').trim(),
          verified: !!reviewer.isVerified,
          date: r.dateCreated || '',
          helpful: Number(r.helpfulCount) || 0,
          media: media
        };
      // A review with no body is not worth a card, whatever else it carries.
      }).filter(function (r) { return r.body && r.rating > 0; });
    }

    /*
     * The five distribution rows the design's summary panel draws, plus the
     * total and average, counted off the reviews we actually hold.
     *
     * Computed rather than read from Okendo's /review_aggregate for two
     * reasons: that endpoint is store-wide, so it can disagree with the
     * product list below it; and it counts reviews we drop (no body, no
     * card). Counting the list guarantees the stated total is exactly the
     * number a shopper can page through — which is the property that broke.
     *
     * Percentages are of the real total, so a level with no reviews reads 0%
     * and its pill can be disabled rather than pretending to be clickable.
     */
    function summarize(list) {
      var total = list.length;
      var byLevel = {};
      var ratingSum = 0;
      var mediaCount = 0;
      list.forEach(function (r) {
        byLevel[r.rating] = (byLevel[r.rating] || 0) + 1;
        ratingSum += r.rating;
        mediaCount += r.media.length;
      });
      var levels = [5, 4, 3, 2, 1].map(function (n) {
        var count = byLevel[n] || 0;
        return {
          stars: n,
          count: count,
          percent: total > 0 ? Math.round((count / total) * 100) : 0
        };
      });
      return {
        total: total,
        average: total > 0 ? (ratingSum / total) : 0,
        mediaCount: mediaCount,
        levels: levels
      };
    }

    /*
     * The key carries the product scope and a version tag. The v1 key held a
     * 12-review payload for an hour and did not mention the size, so without
     * the bump a returning shopper would keep seeing 12 reviews for up to an
     * hour after this fix shipped.
     */
    function cacheKey(sid, pid) { return 'sn-oke-v3-' + sid + (pid ? ':p' + pid : ''); }

    function readCache(sid, pid) {
      try {
        var hit = window.sessionStorage.getItem(cacheKey(sid, pid));
        if (!hit) return null;
        var parsed = JSON.parse(hit);
        if (!parsed.t || (Date.now() - parsed.t) > CACHE_TTL_MS) return null;
        return parsed.d;
      } catch (err) {
        return null;   // private mode, quota, corrupt entry — just refetch
      }
    }

    function writeCache(sid, pid, data) {
      try {
        window.sessionStorage.setItem(cacheKey(sid, pid), JSON.stringify({ t: Date.now(), d: data }));
      } catch (err) {
        /* not being able to cache is not a reason to fail */
      }
    }

    function getJSON(url) {
      return fetch(url, { credentials: 'omit' }).then(function (res) {
        if (!res.ok) throw new Error('okendo ' + res.status);
        return res.json();
      });
    }

    /*
     * One request per page load at most, shared by every surface that asks:
     * the carousel and the All-Reviews dialog use the same payload, and the
     * result is cached in sessionStorage across navigations.
     *
     * Callers pass the PRODUCT, not a count. Everything the store has comes
     * back (112 reviews is ~25KB gzipped over two pages), so how many to show
     * is a rendering decision made per surface — see the carousel's card cap
     * and the dialog's paging.
     *
     * `productId` may be a Shopify numeric id or empty. Empty falls back to the
     * store-wide list, which is what this used to do: a missing attribute
     * degrades to the old behaviour rather than to no reviews at all.
     */
    function load(subscriberId, productId) {
      if (!subscriberId) return Promise.reject(new Error('no subscriber id'));

      var pid = String(productId == null ? '' : productId).replace(/[^0-9]/g, '');
      var key = subscriberId + '|' + pid;
      if (pending[key]) return pending[key];

      var cached = readCache(subscriberId, pid);
      if (cached) {
        pending[key] = Promise.resolve(cached);
        return pending[key];
      }

      var base = API + encodeURIComponent(subscriberId);
      var path = pid
        ? '/products/shopify-' + encodeURIComponent(pid) + '/reviews'
        : '/reviews';
      /*
       * orderBy stays 'rating desc'. The carousel keeps the first review it
       * meets per reviewer, and the dialog's default "most relevant" sort reads
       * as-fetched, so reordering here would quietly change which reviews both
       * surfaces choose.
       */
      var url = base + path + '?limit=' + PAGE_SIZE +
                '&orderBy=' + encodeURIComponent('rating desc');

      /*
       * PAGINATE. `limit` is a PAGE size, not a total: Okendo answers with at
       * most 100 reviews plus a `nextUrl` when more exist, and this used to
       * take page one and stop. That was invisible while the store had 53
       * reviews — there was no second page — and broke silently the moment it
       * passed 100. Because the sort is `rating desc`, the truncation was not
       * random: page one is all 5-star, so the 2-, 3- and 4-star reviews fell
       * off the site entirely and the list contradicted the badge above it
       * (112 stated, 100 pageable). Measured 2026-09-07: 112 reviews exist —
       * 108x5, 2x4, 1x3, 1x2, average 4.94.
       *
       * Pages are concatenated and de-duplicated by reviewId, then normalize/
       * summarize run once over the whole set, so the totals and distribution
       * are still counted off exactly what a shopper can page through. Ordering
       * is preserved: page N always follows page N-1.
       */
      function collect(next, acc, seen, pages) {
        return getJSON(next).then(function (raw) {
          var list = (raw && raw.reviews) || [];
          for (var i = 0; i < list.length; i++) {
            var id = list[i] && list[i].reviewId;
            if (id && seen[id]) continue;
            if (id) seen[id] = 1;
            acc.push(list[i]);
          }
          var nurl = raw && raw.nextUrl;
          if (nurl && list.length && pages + 1 < MAX_PAGES) {
            /* nextUrl is a path ('/stores/…'), already carrying limit,
               orderBy and its lastEvaluated cursor. */
            var abs = /^https?:/i.test(nurl) ? nurl : API_ROOT + nurl;
            return collect(abs, acc, seen, pages + 1);
          }
          return acc;
        });
      }

      pending[key] = collect(url, [], {}, 0).then(function (rawList) {
        var reviews = normalize({ reviews: rawList });
        var data = { reviews: reviews, aggregate: summarize(reviews) };
        writeCache(subscriberId, pid, data);
        return data;
      }).catch(function (err) {
        // Let a later surface retry rather than inheriting this failure.
        delete pending[key];
        throw err;
      });

      return pending[key];
    }

    return { load: load, realTitle: realTitle };
  })();

  /* ======================================================================
   * FEATURED (PINNED) REVIEWS
   *
   * A handful of reviews are pinned to the top of every review surface, in a
   * fixed order: those positions never move, whoever reviews next. Everything
   * else the store holds follows below them, newest first. This is a
   * REORDERING, not a filter — no review is hidden, dropped or paged away by
   * pinning, and the totals, the star distribution and the filter counts are
   * all still counted off the whole set.
   *
   * The ids are printed onto the band as data-featured-review-ids from
   * snippets/sn-featured-review-ids.liquid, so the carousel, the PDP band's
   * sort/filter controls and the All-reviews dialog all read one list and lead
   * with the same reviews.
   *
   * Matching is on Okendo's own reviewId, never on the reviewer's name or the
   * review's title: a reviewer can edit their words without losing their slot,
   * and two customers who share a display name can never be confused for one
   * another. An id that no longer resolves is skipped rather than leaving a
   * gap, and a band with no ids set is left exactly as it was.
   * ==================================================================== */
  function snFeaturedIds(band) {
    return (band.getAttribute('data-featured-review-ids') || '')
      .split(/[\s,]+/)
      .filter(Boolean);
  }

  /* The pinned reviews that are actually present in `list`, in the ids' own
     order. Deduplicated, so the same id written twice still yields one card. */
  function snPickFeatured(list, ids) {
    if (!ids.length) return [];
    var byId = {};
    list.forEach(function (r) { if (r.id) byId[String(r.id)] = r; });
    var out = [];
    var taken = {};
    ids.forEach(function (id) {
      var r = byId[id];
      if (r && !taken[id]) { taken[id] = 1; out.push(r); }
    });
    return out;
  }

  /*
   * Hoists the pinned reviews to the front of an already-ordered list. The rest
   * keeps the order it arrived in, minus the ones now pinned — so a featured
   * review appears exactly once, at its fixed position, and never again further
   * down. Removal is by reviewId.
   *
   * Applied AFTER sorting and filtering rather than before, so no sort can push
   * a pinned review out of its position. A filter legitimately can: a review
   * that is not in the filtered list cannot be pinned to the top of it, which
   * is why "4 stars" shows four-star reviews and not the five-star pins.
   */
  function snPinFeatured(list, ids) {
    var featured = snPickFeatured(list, ids);
    if (!featured.length) return list.slice();
    var pinned = {};
    featured.forEach(function (r) { pinned[String(r.id)] = 1; });
    return featured.concat(list.filter(function (r) { return !pinned[String(r.id)]; }));
  }

  function snNewestFirst(list) {
    return list.slice().sort(function (a, b) {
      return (b.date || '').localeCompare(a.date || '');
    });
  }

  var SN_REVIEW_MORE = 'Read more';
  var SN_REVIEW_LESS = 'Read less';

  /*
   * Decides, per card, whether "Read more" is worth showing.
   *
   * The quote is clamped to three lines in sn-review-cards.liquid because in a
   * flex row the longest review sets the height of every card — one 900-character
   * review made all of them 919px tall on the published homepage. But a link
   * that expands nothing is worse than no link, so the button appears only where
   * the clamp is actually hiding text.
   *
   * Called whenever the answer can change, which is more often than it looks:
   * after every render, once the display face has really loaded (a fallback face
   * wraps at different points, so measuring before then puts the button on the
   * wrong cards), and on resize — the card is clamp(320px,36cqw,640px) wide, so
   * dragging the window changes how many lines a review takes. Cards the shopper
   * has already opened are skipped: re-collapsing a review someone is reading is
   * not a correction.
   */
  function snSyncReviewClamps(root) {
    if (!root) return;
    var cards = root.querySelectorAll('.po-review-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var quote = card.querySelector('p');
      var more = card.querySelector('[data-po-review-more]');
      if (!quote || !more || card.classList.contains('is-expanded')) continue;
      // A pixel of slack: fractional line heights make a quote that fits
      // exactly report a scrollHeight a hair taller than its own box.
      more.hidden = quote.scrollHeight <= quote.clientHeight + 1;
    }
  }

  /*
   * Builds one review card in the design's shape. Text goes in via textContent
   * only — review bodies and reviewer names are user-generated content that must
   * never be parsed as markup.
   *
   * Two variants, decided by the data rather than by a setting: reviews with a
   * customer photo get the photo pane the design shows; those without get a
   * text-only card. A minority of this store's reviews carry media (14 photos
   * and one video, which renders as its poster frame); inventing stock
   * photography for the other 44 was never an option.
   */
  function snBuildReviewCard(review, isDuplicate) {
    var card = document.createElement('article');
    card.className = 'po-review-card';
    if (!review.media.length) card.classList.add('po-review-card--nophoto');
    if (isDuplicate) {
      card.classList.add('po-review-card--dup');
      card.setAttribute('aria-hidden', 'true');
    }

    if (review.media.length) {
      var photo = document.createElement('div');
      photo.className = 'po-review-photo';
      var img = document.createElement('img');
      img.src = review.media[0];
      img.loading = 'lazy';
      img.decoding = 'async';
      // The photo carries no information the review text doesn't; naming the
      // reviewer would just make a screen reader read them twice.
      img.alt = '';
      photo.appendChild(img);
      card.appendChild(photo);
    }

    var body = document.createElement('div');
    body.className = 'po-review-body';

    var a11y = document.createElement('span');
    a11y.className = 'visually-hidden';
    a11y.textContent = review.rating + ' out of 5 stars';
    body.appendChild(a11y);

    var stars = document.createElement('span');
    stars.className = 'po-stars';
    stars.setAttribute('aria-hidden', 'true');
    // Real rating, not a hardcoded five — every current review is 5★ but the
    // next one might not be.
    stars.textContent = '★★★★★'.slice(0, review.rating) + '☆☆☆☆☆'.slice(0, 5 - review.rating);
    body.appendChild(stars);

    // No headline element at all when there is no human title. See realTitle().
    if (review.title) {
      var h3 = document.createElement('h3');
      h3.textContent = review.title;
      body.appendChild(h3);
    }

    var quote = document.createElement('p');
    quote.textContent = '“' + review.body + '”';
    body.appendChild(quote);

    /*
     * "Read more" ships on every card and hides itself on the ones whose quote
     * already fits — snSyncReviewClamps makes that call once the card is in the
     * document and can be measured. Built here, rather than injected after the
     * fact, so a card is never briefly in the DOM as incomplete markup.
     *
     * The second, duplicated set of cards exists only so the desktop marquee can
     * loop without a seam, and is aria-hidden; a focusable control inside
     * aria-hidden content is a trap for keyboard users, so those copies keep the
     * button (it is visible, and clickable with a mouse) but out of the tab order.
     */
    var more = document.createElement('button');
    more.type = 'button';
    more.className = 'po-review-more';
    more.hidden = true;
    more.setAttribute('data-po-review-more', '');
    more.setAttribute('aria-expanded', 'false');
    more.textContent = SN_REVIEW_MORE;
    if (isDuplicate) more.tabIndex = -1;
    body.appendChild(more);

    var meta = document.createElement('div');
    meta.className = 'po-review-meta';
    if (review.name) {
      var who = document.createElement('span');
      who.textContent = review.name;
      meta.appendChild(who);
    }
    // "Verified buyer" is a claim about a real purchase, so it appears only when
    // Okendo says the reviewer is verified.
    if (review.verified) {
      var vb = document.createElement('span');
      vb.textContent = 'Verified buyer';
      meta.appendChild(vb);
    }
    if (meta.childNodes.length) body.appendChild(meta);

    card.appendChild(body);
    return card;
  }

  /* ---------------------------------------------------------------------
   * snSortMenu — drives the "Sort by" listbox in snippets/sn-sort-menu.liquid.
   *
   * The design's sort control is not a <select>: it is a button plus a bordered
   * card of options, anchored to the trigger's RIGHT edge, with the chosen row
   * filled banana and ticked. A native select can't render any of that, and the
   * OS anchors its list to the control's left edge, which is why the shipped
   * dropdown opened on the wrong side.
   *
   * Behaviour is transcribed from the design source's openSortMenu /
   * closeSortMenu / onSortOptionKeyDown: flip above the trigger when there is
   * under 230px below it, close on outside mousedown, Escape or Tab, and put
   * focus on the currently chosen row when the menu opens.
   *
   * Returns { value } — call it for the current mode. onChange fires only when
   * the shopper picks a different mode than the one already set.
   * ------------------------------------------------------------------- */
  /*
   * The mode the control starts on, and the answer for a page that renders no
   * control at all. 'newest' rather than 'relevant': below the pinned reviews
   * the list reads as a feed, so the next review a shopper sees should be the
   * most recent one. Must match data-sn-sort-value in snippets/sn-sort-menu.liquid.
   */
  var SN_SORT_DEFAULT = 'newest';

  function snSortMenu(wrap, onChange) {
    // No control on the page (the dialog renders without one on some templates)
    // still has to answer applySort(), so hand back the default rather than null.
    if (!wrap) return { value: function () { return SN_SORT_DEFAULT; } };

    var trigger = wrap.querySelector('[data-sn-sort-trigger]');
    var menu = wrap.querySelector('[data-sn-sort-menu]');
    var labelEl = wrap.querySelector('[data-sn-sort-label]');
    var opts = Array.prototype.slice.call(wrap.querySelectorAll('[data-sn-sort-opt]'));
    if (!trigger || !menu || !opts.length) return { value: function () { return SN_SORT_DEFAULT; } };

    // Held so they can be detached again; a listener left on document would keep
    // closing a menu that no longer exists.
    var outsideHandler = null;
    var escHandler = null;

    function value() { return wrap.getAttribute('data-sn-sort-value') || SN_SORT_DEFAULT; }

    function open() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var below = vh - trigger.getBoundingClientRect().bottom;
      wrap.setAttribute('data-sn-sort-flip', below < 230 ? 'up' : 'down');

      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');

      outsideHandler = function (e) { if (!wrap.contains(e.target)) close(false); };
      document.addEventListener('mousedown', outsideHandler);
      escHandler = function (e) { if (e.key === 'Escape') close(true); };
      document.addEventListener('keydown', escHandler);

      var current = null;
      opts.forEach(function (o) { if (o.getAttribute('aria-selected') === 'true') current = o; });
      (current || opts[0]).focus();
    }

    function close(returnFocus) {
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (outsideHandler) { document.removeEventListener('mousedown', outsideHandler); outsideHandler = null; }
      if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
      if (returnFocus) trigger.focus();
    }

    function select(opt) {
      var next = opt.getAttribute('data-sn-sort-opt');
      var changed = next !== value();
      wrap.setAttribute('data-sn-sort-value', next);
      opts.forEach(function (o) { o.setAttribute('aria-selected', String(o === opt)); });
      if (labelEl) labelEl.textContent = opt.querySelector('span').textContent;
      close(true);
      if (changed && onChange) onChange();
    }

    trigger.addEventListener('click', function () {
      if (menu.hidden) open(); else close(false);
    });
    // preventDefault matters: without it Enter and Space also fire the click
    // above, which would close the menu the same tick it opened.
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    });

    opts.forEach(function (opt, i) {
      opt.addEventListener('click', function () { select(opt); });
      opt.addEventListener('keydown', function (e) {
        var n = opts.length;
        if (e.key === 'ArrowDown') { e.preventDefault(); opts[(i + 1) % n].focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); opts[(i - 1 + n) % n].focus(); }
        else if (e.key === 'Home') { e.preventDefault(); opts[0].focus(); }
        else if (e.key === 'End') { e.preventDefault(); opts[n - 1].focus(); }
        else if (e.key === 'Tab') { close(false); }
      });
    });

    return { value: value };
  }

  /* ----------------------------------------------------------------------
   * Reviews carousel — the marquee runs at EVERY width (2026-08-25 mobile
   * pass: the phone carousel must move on its own, not only when swiped).
   *
   * The track is translated at 22px/s and wraps at half its width (the card
   * list is rendered twice, so the second half repeats the first and the seam
   * is invisible). Pauses on hover (hover-capable pointers only — a phone tap
   * fires a compatibility mouseenter with no mouseleave, which would stop it
   * for good), on arrow focus, while dragging, and while the tab is hidden;
   * arrow clicks and drags pause it and resume after a beat so it doesn't
   * yank away from someone reading. Touch drags reach the pointer handlers
   * because the track is touch-action:pan-y — horizontal swipes scrub the
   * marquee, vertical swipes still scroll the page.
   *
   * prefers-reduced-motion is the one exception: phones fall back to the
   * native horizontal scroller (see sn-review-cards.liquid) with the arrows
   * paging it; desktop simply doesn't animate.
   *
   * Called AFTER the cards are in the DOM, never at parse time: the wrap maths
   * divides track.scrollWidth by two, which is zero on an empty track and would
   * leave the marquee translating nothing forever. Card images load later and
   * change that width, which is why wrap() re-measures every frame instead of
   * caching a half-width.
   * -------------------------------------------------------------------- */
  function snStartMarquee() {
    var track = document.getElementById('sn-po-reviews-track');
    if (!track) return;
    // Re-renders (the PDP band's filter chips rebuild the card list) call this
    // again; the listeners below live on nodes that persist, so a second full
    // init would double-bind them. Rewinding the existing instance is enough.
    if (track._snMarqueeReset) { track._snMarqueeReset(); return; }
    var viewport = track.parentElement;
    if (!viewport) return;
    var prev = document.querySelector('[data-po-review-prev]');
    var next = document.querySelector('[data-po-review-next]');

    var SPEED = 22;             // px per second — matches the design
    var RESUME_AFTER = 7000;    // ms to wait before auto-scroll resumes after interaction
    /*
     * 700px no longer switches marquee vs scroller (the marquee runs at every
     * width) — the query stays only so crossing the breakpoint re-syncs the
     * arrows and clears reduced-motion scroller residue via applyMode.
     */
    var desktopQuery = window.matchMedia('(min-width: 700px)');
    var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var hoverQuery = window.matchMedia('(hover: hover)');

    var st = {
      x: 0,
      paused: false,
      dragging: false,
      dragStartX: 0,
      dragStartX0: 0,
      raf: null,
      lastTs: null,
      resumeTimer: null,
      transitionTimer: null,
    };

    track._snMarqueeReset = function () {
      st.x = 0;
      st.lastTs = null;
      track.style.transform = 'translateX(0px)';
    };

    function marqueeActive() {
      return !reduceQuery.matches;
    }

    function gap() {
      return parseFloat(getComputedStyle(track).columnGap) || 24;
    }

    /* Wrap so the translation never runs past the duplicated half. Uses a
     * while loop rather than a single correction because a long pause (a
     * background tab, a slow frame) can overshoot by more than one half. */
    function wrap() {
      var half = track.scrollWidth / 2;
      if (half <= 0) return;
      while (-st.x >= half) st.x += half;
      while (st.x > 0) st.x -= half;
    }

    function apply() {
      track.style.transform = 'translateX(' + st.x + 'px)';
    }

    function tick(ts) {
      if (st.lastTs == null) st.lastTs = ts;
      // Clamp dt so returning to a backgrounded tab doesn't jump the track.
      var dt = Math.min((ts - st.lastTs) / 1000, 0.1);
      st.lastTs = ts;
      if (!st.paused && !st.dragging) {
        st.x -= SPEED * dt;
        wrap();
        apply();
      }
      st.raf = requestAnimationFrame(tick);
    }

    /*
     * AUTOPLAY IS OFF (2026-09-04), and this is the only change made to the
     * original carousel.
     *
     * `tick()` below is the sole source of automatic movement: it was scheduled
     * from here on a requestAnimationFrame loop and advanced `st.x` by SPEED
     * every frame. Neutering the scheduler — rather than deleting tick(), the
     * arrows, the drag handlers or any of the CSS — is what keeps the rest of
     * the original carousel byte-for-byte intact:
     *
     *   - the ARROWS still work, because step() manipulates the transform
     *     directly (pause -> set st.x -> wrap -> apply) and never depended on
     *     the loop running;
     *   - DRAG-to-scrub still works, for the same reason;
     *   - wrap() still gives the duplicated card list its seamless loop, so
     *     paging past the end wraps exactly as the original design did;
     *   - pause() / resumeIn() / the hover and visibilitychange handlers are
     *     now inert no-ops (they only ever gated the loop), so they cost
     *     nothing and are left untouched rather than surgically unpicked.
     *
     * Net effect: the carousel is identical to the original in layout, cards,
     * arrows, spacing, data and responsive behaviour, and moves ONLY when the
     * user drives it. Do not reinstate the requestAnimationFrame call here.
     */
    function startMarquee() {
      return;
    }

    function stopMarquee() {
      if (st.raf != null) {
        cancelAnimationFrame(st.raf);
        st.raf = null;
      }
      // Hand the track back to CSS so the mobile scroller isn't offset by a
      // leftover transform.
      track.style.transform = '';
      track.style.transition = '';
      st.x = 0;
    }

    function pause() {
      if (st.resumeTimer) {
        clearTimeout(st.resumeTimer);
        st.resumeTimer = null;
      }
      st.paused = true;
    }

    function resumeIn(delay) {
      if (st.resumeTimer) clearTimeout(st.resumeTimer);
      st.resumeTimer = setTimeout(function () {
        st.paused = false;
        st.lastTs = null;
        st.resumeTimer = null;
      }, delay || 0);
    }

    /* One card-width step. On desktop this nudges the transform with a bouncy
     * transition; on mobile it scrolls the native container instead. */
    function step(dir) {
      var card = track.querySelector('.po-review-card');
      var amount = (card ? card.getBoundingClientRect().width : 320) + gap();

      if (!marqueeActive()) {
        viewport.scrollBy({ left: dir * amount });
        setTimeout(syncArrows, 450);
        return;
      }

      pause();
      track.style.transition = 'transform .45s cubic-bezier(0.34,1.56,0.64,1)';
      st.x += dir === 1 ? -amount : amount;
      wrap();
      apply();
      if (st.transitionTimer) clearTimeout(st.transitionTimer);
      st.transitionTimer = setTimeout(function () {
        track.style.transition = '';
      }, 460);
      resumeIn(RESUME_AFTER);
    }

    /* Arrows only ever disable in the mobile scroller, where the list has real
     * ends. The marquee loops forever, so disabling there would be wrong. */
    function syncArrows() {
      if (marqueeActive()) {
        if (prev) prev.disabled = false;
        if (next) next.disabled = false;
        return;
      }
      var max = viewport.scrollWidth - viewport.clientWidth - 1;
      if (prev) prev.disabled = viewport.scrollLeft <= 0;
      if (next) next.disabled = viewport.scrollLeft >= max;
    }

    if (prev) {
      prev.addEventListener('click', function () { step(-1); });
      prev.addEventListener('focus', pause);
      prev.addEventListener('blur', function () { resumeIn(0); });
    }
    if (next) {
      next.addEventListener('click', function () { step(1); });
      next.addEventListener('focus', pause);
      next.addEventListener('blur', function () { resumeIn(0); });
    }

    track.addEventListener('mouseenter', function () {
      if (marqueeActive() && hoverQuery.matches) pause();
    });
    track.addEventListener('mouseleave', function () {
      if (marqueeActive() && hoverQuery.matches) resumeIn(0);
    });

    /* Drag to scrub the marquee — reduced-motion mode uses native scrolling. */
    track.addEventListener('pointerdown', function (e) {
      if (!marqueeActive()) return;
      /*
       * Never begin a drag on a control inside a card. The drag below calls
       * setPointerCapture on the track, and Chrome then retargets the
       * compatibility mouse events — `click` among them — to the capture
       * element, so a steady-handed click on a card's "Read more" would be
       * delivered to the track and the button would never fire — verified by
       * re-enabling the capture and dispatching a real click: nothing expanded.
       * Grabbing the paper to scrub still works; grabbing a button now just
       * presses it.
       */
      if (e.target && e.target.closest && e.target.closest('button,a')) return;
      st.dragging = true;
      pause();
      st.dragStartX = e.clientX;
      st.dragStartX0 = st.x;
      track.style.transition = '';
      track.classList.add('is-dragging');
      if (track.setPointerCapture && e.pointerId != null) {
        try { track.setPointerCapture(e.pointerId); } catch (err) { /* not capturable */ }
      }
    });
    track.addEventListener('pointermove', function (e) {
      if (!st.dragging) return;
      st.x = st.dragStartX0 + (e.clientX - st.dragStartX);
      wrap();
      apply();
    });
    function endDrag() {
      if (!st.dragging) return;
      st.dragging = false;
      track.classList.remove('is-dragging');
      resumeIn(RESUME_AFTER);
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Don't burn frames animating a tab nobody is looking at.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        st.paused = true;
      } else if (marqueeActive()) {
        st.paused = false;
        st.lastTs = null;
      }
    });

    viewport.addEventListener('scroll', syncArrows, { passive: true });

    function applyMode() {
      if (marqueeActive()) {
        // Leftover scrollLeft from the reduced-motion scroller would offset
        // the transform animation; the marquee owns the viewport now.
        viewport.scrollLeft = 0;
        startMarquee();
      } else {
        stopMarquee();
      }
      syncArrows();
    }

    // Re-evaluate when the viewport crosses the breakpoint or motion
    // preference changes, so rotating a tablet switches modes cleanly.
    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', applyMode);
      reduceQuery.addEventListener('change', applyMode);
    } else if (desktopQuery.addListener) {
      desktopQuery.addListener(applyMode);
      reduceQuery.addListener(applyMode);
    }
    window.addEventListener('resize', syncArrows);

    applyMode();
  }

  /* ---------------- Reviews carousel: fetch, build, then animate ---------------- */
  (function initReviewsCarousel() {
    var band = document.getElementById('sn-po-reviews') || document.getElementById('sn-pdp-reviews');
    if (!band) return;
    var track = document.getElementById('sn-po-reviews-track');
    if (!track) return;

    var sid = band.getAttribute('data-oke-subscriber-id');
    var pid = band.getAttribute('data-product-id');
    /*
     * How many CARDS the carousel shows — not how many reviews are fetched.
     * The whole set is fetched either way and the All-reviews dialog lists all
     * of it; the carousel is a highlight reel, and 110 duplicated cards on a
     * phone would be a strange way to state the review total.
     */
    var cardCap = Math.max(1, parseInt(band.getAttribute('data-review-limit'), 10) || 12);
    var carousel = band.querySelector('[data-po-reviews-carousel]');
    // The pinned review ids, in display order — see snPinFeatured. Both this
    // band and the PDP one print them, so the two lead with the same reviews.
    var featuredIds = snFeaturedIds(band);

    /*
     * Nothing is shown until real reviews arrive. The alternative — markup that
     * animates an empty track, or skeleton cards that never fill — reads as
     * broken on a slow connection, and this band already carries the heading and
     * the server-rendered rating badge, so it is never empty of meaning.
     */
    function giveUp() {
      if (carousel) carousel.remove();
    }

    if (!sid || !window.fetch || !window.Promise) { giveUp(); return; }

    /*
     * One card per reviewer.
     *
     * Okendo holds two reviews each from Arun G., Michael J. and Richard L., so
     * the carousel showed the same name twice in a row and read as though it
     * were repeating itself. Both reviews are genuine and both stay in the
     * All-reviews list, which is the complete record — but a scrolling sample
     * should not spend two of its slots on one person. The reviews become a pool
     * of 50 cards this way, of which the carousel shows its card cap.
     *
     * Keeps the strongest review per person: one with a photo first, then the
     * longer body, then the more recent. Matching is on the exact display name;
     * this store also has a "Catherina" and a "Catherina W." who are plausibly the
     * same customer, but collapsing those would be guessing at identity from a
     * truncated surname, so they are left alone.
     *
     * `skipNames` holds the reviewers already pinned to the front of the
     * carousel. Their other reviews are dropped from the remainder for exactly
     * the reason above — a scrolling sample should not spend two slots on one
     * person, least of all on someone it has already led with. This is the
     * carousel's own name-based rule and is separate from pinning itself, which
     * only ever matches on reviewId. The All-reviews dialog applies neither and
     * remains the complete record.
     */
    function onePerReviewer(list, skipNames) {
      var best = {};
      var order = [];
      list.forEach(function (r) {
        var key = (r.name || '').trim().toLowerCase();
        if (key && skipNames && skipNames[key]) return;   // already pinned above
        if (!key) { order.push(r); return; }        // anonymous: never merged
        var held = best[key];
        if (!held) { best[key] = r; order.push(r); return; }
        var better = (r.media.length > held.media.length) ||
                     (r.media.length === held.media.length && r.body.length > held.body.length) ||
                     (r.media.length === held.media.length && r.body.length === held.body.length &&
                      (r.date || '') > (held.date || ''));
        if (better) {
          order[order.indexOf(held)] = r;
          best[key] = r;
        }
      });
      return order;
    }

    /*
     * There is no photos-first pass any more.
     *
     * It existed to make sure a photographed stack of someone else's pancakes
     * led the carousel rather than whichever review happened to sit inside the
     * first `cardCap` of the rating-sorted fetch — a merchandising choice. The
     * pinned reviews now do that job explicitly and better: all of them carry a
     * customer photo and they are chosen rather than inferred. Below them the
     * order is the one the brief asks for and the one a shopper expects of a
     * feed — newest first — which a photo partition would silently override.
     */

    /*
     * Renders the card list into the track — TWICE. The desktop marquee wraps
     * at half the track width, so the second copy is what makes the seam
     * invisible as it loops. It is aria-hidden and display:none on mobile,
     * where the track is a plain scroller and reading the same reviews twice
     * would just be confusing.
     */
    function renderTrack(fullList) {
      // Capped here rather than at the call sites so no future path — a filter
      // chip, a sort change, the pinned list — can slip past it.
      var list = fullList.slice(0, cardCap);
      track.textContent = '';
      var frag = document.createDocumentFragment();
      for (var pass = 0; pass < 2; pass++) {
        for (var i = 0; i < list.length; i++) {
          var card = snBuildReviewCard(list[i], pass === 1);
          /*
           * Both copies of a review carry the same key, so expanding one expands
           * the other. Without it the marquee can show the same review twice at
           * two different heights — one opened, one not — which reads as two
           * different reviews that happen to start the same way.
           */
          card.setAttribute('data-po-review-key', String(i));
          frag.appendChild(card);
        }
      }
      track.appendChild(frag);
    }

    /*
     * Re-decides every card's "Read more". Reading scrollHeight forces the
     * layout it needs, so there is no rAF hop here — but WHERE this is called
     * from matters, and got it wrong once:
     *
     * The carousel ships `hidden` and is revealed only once reviews arrive. In a
     * display:none subtree clientHeight and scrollHeight are both 0, so `0 <= 0`
     * says every quote fits and every button hides itself — including the one on
     * the 900-character review the clamp exists for. Called from inside
     * renderTrack (before the reveal) that is exactly what happened: 24 of 24
     * buttons hidden, and only a later window resize put them back. So this is
     * called AFTER the carousel is on screen, never before.
     *
     * fonts.ready is re-armed on every pass rather than hooked once at init:
     * the webfont settles in a few hundred milliseconds while the Okendo fetch
     * takes longer, so a single init-time hook usually resolves before a card
     * exists and corrects nothing.
     */
    function refreshClamps() {
      snSyncReviewClamps(track);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { snSyncReviewClamps(track); });
      }
    }

    /*
     * One delegated listener for every card's "Read more", bound once — the
     * cards themselves are replaced wholesale on each filter or sort change,
     * so anything bound per button would have to be rebound with them.
     */
    track.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-po-review-more]') : null;
      if (!btn) return;
      var card = btn.closest('.po-review-card');
      if (!card) return;
      var open = !card.classList.contains('is-expanded');
      var key = card.getAttribute('data-po-review-key');
      var group = key === null
        ? [card]
        : track.querySelectorAll('[data-po-review-key="' + key + '"]');
      for (var i = 0; i < group.length; i++) {
        var twin = group[i];
        twin.classList.toggle('is-expanded', open);
        var twinBtn = twin.querySelector('[data-po-review-more]');
        if (!twinBtn) continue;
        twinBtn.setAttribute('aria-expanded', String(open));
        twinBtn.textContent = open ? SN_REVIEW_LESS : SN_REVIEW_MORE;
      }
      // Collapsed again, the quote may now fit — re-decide rather than leaving a
      // "Read more" on a review that no longer has more.
      if (!open) snSyncReviewClamps(track);
    });

    /*
     * A card is clamp(320px,36cqw,640px) wide, so dragging the window changes
     * how many lines a review takes and therefore which cards have more to
     * show. Debounced: each pass reads scrollHeight on every card in the track.
     */
    var clampTimer = null;
    window.addEventListener('resize', function () {
      if (clampTimer) clearTimeout(clampTimer);
      clampTimer = setTimeout(function () { snSyncReviewClamps(track); }, 150);
    });

    /*
     * The PDP band's on-page summary panel and filter/sort controls, from the
     * design. The homepage band has neither, so every hook is optional. The
     * average and count are already server-rendered from the metafield; the
     * live figures are recomputed from the fetched reviews so the panel, the
     * badge and the list all state one number.
     */
    function initBandExtras(reviews, aggregate) {
      // Refresh the server-rendered figures from the live aggregate, formatted
      // exactly as the All-reviews dialog formats them, so the two agree.
      if (aggregate && aggregate.total) {
        var avgEl = band.querySelector('[data-pr-avg]');
        if (avgEl) avgEl.textContent = (Math.round(aggregate.average * 10) / 10).toFixed(1) + '/5';
        var countEl = band.querySelector('[data-pr-count]');
        if (countEl) countEl.textContent = aggregate.total + ' review' + (aggregate.total === 1 ? '' : 's');
      }
      var dist = band.querySelector('[data-pr-dist]');
      if (dist && aggregate && aggregate.total) {
        dist.textContent = '';
        aggregate.levels.forEach(function (lvl) {
          var row = document.createElement('div');
          row.className = 'pr-dist-row';
          row.setAttribute('role', 'img');
          row.setAttribute('aria-label', lvl.count + ' of ' + aggregate.total +
            ' reviews are ' + lvl.stars + ' star' + (lvl.stars === 1 ? '' : 's') +
            ' (' + lvl.percent + '%)');

          var label = document.createElement('span');
          label.className = 'pr-dist-label';
          label.textContent = lvl.stars + ' star';
          row.appendChild(label);

          var trackEl = document.createElement('span');
          trackEl.className = 'pr-dist-track';
          var fill = document.createElement('span');
          fill.className = 'pr-dist-fill';
          fill.style.width = lvl.percent + '%';
          trackEl.appendChild(fill);
          row.appendChild(trackEl);

          var pct = document.createElement('span');
          pct.className = 'pr-dist-pct';
          pct.textContent = lvl.percent + '%';
          row.appendChild(pct);
          dist.appendChild(row);
        });
      }

      var controls = band.querySelector('[data-pr-controls]');
      if (!controls) return;
      var chips = Array.prototype.slice.call(controls.querySelectorAll('[data-pr-filter]'));
      // The control drives itself and calls refresh() on a new pick; there is no
      // 'change' event to bind because it is a listbox, not a <select>.
      var sortMenu = snSortMenu(controls.querySelector('[data-sn-sort]'), refresh);
      var filter = 'all';

      function applyFilter(list) {
        if (filter === 'photos') return list.filter(function (r) { return r.media.length > 0; });
        if (filter === 'recent') {
          return list.slice().sort(function (a, b) { return (b.date || '').localeCompare(a.date || ''); });
        }
        if (filter === '5' || filter === '4') {
          var want = parseInt(filter, 10);
          return list.filter(function (r) { return r.rating === want; });
        }
        return list;
      }

      // Same rules as the All-reviews dialog, so the band and the dialog can
      // never order the same reviews differently.
      function applySort(list) {
        var mode = sortMenu.value();
        var out = list.slice();
        if (mode === 'newest') {
          out.sort(function (a, b) { return (b.date || '').localeCompare(a.date || ''); });
        } else if (mode === 'highest') {
          out.sort(function (a, b) { return b.rating - a.rating; });
        } else if (mode === 'lowest') {
          out.sort(function (a, b) { return a.rating - b.rating; });
        } else {
          out.sort(function (a, b) {
            var am = a.media.length ? 1 : 0, bm = b.media.length ? 1 : 0;
            if (am !== bm) return bm - am;
            if (a.helpful !== b.helpful) return b.helpful - a.helpful;
            return (b.date || '').localeCompare(a.date || '');
          });
        }
        return out;
      }

      function refresh() {
        // Pinned last, so neither the chosen sort nor a filter can move a
        // featured review out of its position — see snPinFeatured.
        renderTrack(snPinFeatured(applySort(applyFilter(reviews)), featuredIds));
        snStartMarquee();
        // The controls only exist once the carousel is up, so measuring here is
        // always measuring something on screen.
        refreshClamps();
      }

      chips.forEach(function (chip) {
        // A chip with nothing behind it stays visible (the design shows the
        // full set) but does not pretend to be live.
        var val = chip.getAttribute('data-pr-filter');
        var count;
        if (val === 'photos') count = reviews.filter(function (r) { return r.media.length > 0; }).length;
        else if (val === '5' || val === '4') count = reviews.filter(function (r) { return r.rating === parseInt(val, 10); }).length;
        else count = reviews.length;
        if (count === 0) chip.disabled = true;

        chip.addEventListener('click', function () {
          filter = val;
          chips.forEach(function (c) { c.setAttribute('aria-pressed', String(c === chip)); });
          refresh();
        });
      });

      controls.hidden = false;
    }

    snReviews.load(sid, pid).then(function (data) {
      if (!data.reviews.length) { giveUp(); return; }

      /*
       * Pinned reviews first, in their fixed order, then everything else the
       * store holds, newest first.
       *
       * The pins are an instruction, so the card cap is widened to fit them
       * rather than allowed to cut into them — a cap of 3 with six pinned
       * reviews would drop three of the six without saying so.
       */
      var featured = snPickFeatured(data.reviews, featuredIds);
      cardCap = Math.max(cardCap, featured.length);

      var pinnedIds = {};
      var pinnedNames = {};
      featured.forEach(function (r) {
        pinnedIds[String(r.id)] = 1;
        if (r.name) pinnedNames[r.name.trim().toLowerCase()] = 1;
      });
      // By id, so a pinned review can never also appear in the remainder.
      var rest = data.reviews.filter(function (r) { return !pinnedIds[String(r.id)]; });

      /*
       * The filter chips and the summary panel get the FULL pool, so "With
       * photos (9)" counts every review with a photo rather than every one that
       * happened to fit in the carousel. renderTrack applies the cap.
       */
      var pool = featured.concat(onePerReviewer(snNewestFirst(rest), pinnedNames));

      renderTrack(pool);
      if (carousel) carousel.hidden = false;
      initBandExtras(pool, data.aggregate);

      snStartMarquee();
      // After the reveal above, not before — see refreshClamps.
      refreshClamps();
    }).catch(function () {
      giveUp();
    });
  })();

  /* ---------------- All-reviews dialog ---------------- */
  (function initReviewsDialog() {
    var modal = document.getElementById('sn-reviews-modal');
    if (!modal) return;
    var band = document.getElementById('sn-po-reviews') || document.getElementById('sn-pdp-reviews');
    if (!band) return;

    var sid = band.getAttribute('data-oke-subscriber-id');
    var pid = band.getAttribute('data-product-id');
    // The same pinned list the band leads with, read off the same attribute, so
    // opening the dialog never reshuffles the reviews the shopper just saw.
    var featuredIds = snFeaturedIds(band);
    var openers = Array.prototype.slice.call(document.querySelectorAll('[data-po-open-reviews]'));
    if (!sid || !window.fetch || !window.Promise) {
      /*
       * Without data there is nothing to open, so the affordance goes. Openers
       * that are ONLY an affordance ("Read all reviews") are removed outright —
       * a link that opens an empty screen is worse than no link.
       *
       * `data-po-keep` marks an opener that also carries CONTENT which must
       * survive: the buy box's rating line is the server-rendered Okendo star
       * rating, true whether or not the browser can reach the reviews API, and
       * deleting it would take the social proof off the page along with the
       * link. Those are stripped back to inert text instead — href, role and
       * label removed so nothing announces or offers a click.
       */
      openers.forEach(function (o) {
        if (!o.hasAttribute('data-po-keep')) { o.remove(); return; }
        o.removeAttribute('href');
        o.removeAttribute('aria-label');
        o.removeAttribute('data-po-open-reviews');
        o.style.cursor = 'default';
      });
      return;
    }

    var listEl = modal.querySelector('[data-rm-list]');
    var pillsEl = modal.querySelector('[data-rm-pills]');
    var moreEl = modal.querySelector('[data-rm-more]');
    var emptyEl = modal.querySelector('[data-rm-empty]');
    // Same listbox as the band's; refresh() is hoisted, so passing it here is fine.
    var sortMenu = snSortMenu(modal.querySelector('[data-sn-sort]'), refresh);
    var scrollEl = modal.querySelector('[data-rm-scroll]');

    var PAGE = 6;              // matches the design's six-per-page list
    var all = [];              // every fetched review
    var view = [];             // current filter+sort result
    var shown = 0;
    var filter = 'all';        // 'all' | 'photos' | '5'…'1'
    var loaded = false;
    var lastFocus = null;

    /* ---- rendering ---- */

    function renderStars(n) {
      return '★★★★★'.slice(0, n) + '☆☆☆☆☆'.slice(0, 5 - n);
    }

    function formatDate(iso) {
      if (!iso) return '';
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      // Locale-formatted rather than hand-built, so it reads correctly wherever
      // the shopper is.
      try {
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
      } catch (err) {
        return '';
      }
    }

    function buildRow(review) {
      var card = document.createElement('article');
      card.className = 'rm-card';

      if (review.media.length) {
        var photo = document.createElement('div');
        photo.className = 'rm-card-photo';
        var img = document.createElement('img');
        img.src = review.media[0];
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = '';
        photo.appendChild(img);
        card.appendChild(photo);
      }

      var body = document.createElement('div');
      body.className = 'rm-card-body';

      var a11y = document.createElement('span');
      a11y.className = 'visually-hidden';
      a11y.textContent = review.rating + ' out of 5 stars';
      body.appendChild(a11y);

      var stars = document.createElement('span');
      stars.className = 'rm-stars';
      stars.setAttribute('aria-hidden', 'true');
      stars.textContent = renderStars(review.rating);
      body.appendChild(stars);

      // Same rule as the carousel: no auto-generated "5 Stars" headline, and no
      // element at all when there is no human title.
      if (review.title) {
        var h3 = document.createElement('h3');
        h3.textContent = review.title;
        body.appendChild(h3);
      }

      var p = document.createElement('p');
      p.textContent = '“' + review.body + '”';
      body.appendChild(p);

      var meta = document.createElement('div');
      meta.className = 'rm-card-meta';
      if (review.name) {
        var who = document.createElement('span');
        who.className = 'rm-who';
        who.textContent = review.name;
        meta.appendChild(who);
      }
      if (review.verified) {
        var vb = document.createElement('span');
        vb.className = 'rm-vb';
        vb.textContent = 'Verified buyer';
        meta.appendChild(vb);
      }
      var when = formatDate(review.date);
      if (when) {
        var dt = document.createElement('span');
        dt.className = 'rm-date';
        dt.textContent = when;
        meta.appendChild(dt);
      }
      if (meta.childNodes.length) body.appendChild(meta);

      card.appendChild(body);
      return card;
    }

    /* ---- filter + sort, both over the fetched list ---- */

    function applyFilter(list) {
      if (filter === 'all') return list.slice();
      if (filter === 'photos') {
        return list.filter(function (r) { return r.media.length > 0; });
      }
      var want = parseInt(filter, 10);
      return list.filter(function (r) { return r.rating === want; });
    }

    function applySort(list) {
      var mode = sortMenu.value();
      var out = list.slice();
      if (mode === 'newest') {
        out.sort(function (a, b) { return (b.date || '').localeCompare(a.date || ''); });
      } else if (mode === 'highest') {
        out.sort(function (a, b) { return b.rating - a.rating; });
      } else if (mode === 'lowest') {
        out.sort(function (a, b) { return a.rating - b.rating; });
      } else {
        /*
         * "Most relevant": reviews with a photo first, then the ones other
         * shoppers found helpful, then newest. Okendo's own default sort is
         * has_media desc, so this follows it rather than inventing a ranking.
         */
        out.sort(function (a, b) {
          var am = a.media.length ? 1 : 0, bm = b.media.length ? 1 : 0;
          if (am !== bm) return bm - am;
          if (a.helpful !== b.helpful) return b.helpful - a.helpful;
          return (b.date || '').localeCompare(a.date || '');
        });
      }
      return out;
    }

    function renderPage(reset) {
      if (reset) {
        listEl.textContent = '';
        shown = 0;
      }
      var slice = view.slice(shown, shown + PAGE);
      var frag = document.createDocumentFragment();
      slice.forEach(function (r) { frag.appendChild(buildRow(r)); });
      listEl.appendChild(frag);
      shown += slice.length;

      var left = view.length - shown;
      if (left > 0) {
        // The design's button names the number remaining ("LOAD 4 MORE
        // REVIEWS"), so it counts what is actually left, capped at a page.
        var n = Math.min(left, PAGE);
        moreEl.textContent = 'Load ' + n + ' more review' + (n === 1 ? '' : 's');
        moreEl.hidden = false;
      } else {
        moreEl.hidden = true;
      }
      emptyEl.hidden = view.length > 0;
    }

    function refresh() {
      // Pinned last, for the same reason as the band: the featured reviews hold
      // positions 1..N under every sort, and everything else follows below them
      // — still the complete list, still paged through in full.
      view = snPinFeatured(applySort(applyFilter(all)), featuredIds);
      renderPage(true);
    }

    /* ---- controls ---- */

    function buildPills(aggregate) {
      var defs = [{ key: 'all', label: 'All reviews', count: all.length }];
      var withPhotos = all.filter(function (r) { return r.media.length > 0; }).length;
      defs.push({ key: 'photos', label: 'With photos', count: withPhotos });
      [5, 4, 3, 2, 1].forEach(function (n) {
        var count = all.filter(function (r) { return r.rating === n; }).length;
        defs.push({ key: String(n), label: n + (n === 1 ? ' star' : ' stars'), count: count });
      });

      pillsEl.textContent = '';
      defs.forEach(function (def) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'rm-pill';
        b.setAttribute('aria-pressed', String(def.key === filter));
        b.dataset.rmFilter = def.key;
        b.appendChild(document.createTextNode(def.label));
        var c = document.createElement('span');
        c.className = 'rm-pill-count';
        c.textContent = '(' + def.count + ')';
        b.appendChild(c);
        // Nothing to show behind this pill, so it does not pretend to be live.
        if (def.count === 0) b.disabled = true;
        pillsEl.appendChild(b);
      });
    }

    function renderSummary(aggregate) {
      var summaryEl = modal.querySelector('[data-rm-summary]');
      var aggEl = modal.querySelector('[data-rm-aggregate]');
      if (!aggregate || !aggregate.total) return;

      var avg = aggregate.average;
      var avgText = (Math.round(avg * 10) / 10).toFixed(1);
      var countText = aggregate.total + ' review' + (aggregate.total === 1 ? '' : 's');

      modal.querySelector('[data-rm-avg]').textContent = avgText;
      modal.querySelector('[data-rm-avg-stars]').textContent = renderStars(Math.round(avg));
      modal.querySelector('[data-rm-count]').textContent = countText;
      if (aggEl) aggEl.textContent = avgText + ' · ' + countText;

      var dist = modal.querySelector('[data-rm-dist]');
      dist.textContent = '';
      aggregate.levels.forEach(function (lvl) {
        var row = document.createElement('div');
        row.className = 'rm-dist-row';

        var label = document.createElement('span');
        label.className = 'rm-dist-label';
        label.textContent = lvl.stars + '★';
        row.appendChild(label);

        var track = document.createElement('span');
        track.className = 'rm-dist-track';
        var fill = document.createElement('span');
        fill.className = 'rm-dist-fill';
        // Real percentage of the real total — a level with no reviews is an
        // empty track, not a token sliver.
        fill.style.width = lvl.percent + '%';
        track.appendChild(fill);
        row.appendChild(track);

        var pct = document.createElement('span');
        pct.className = 'rm-dist-pct';
        pct.textContent = lvl.percent + '%';
        row.appendChild(pct);

        // One accessible sentence per row instead of three loose fragments.
        row.setAttribute('role', 'img');
        row.setAttribute('aria-label', lvl.count + ' of ' + aggregate.total +
          ' reviews are ' + lvl.stars + ' star' + (lvl.stars === 1 ? '' : 's') +
          ' (' + lvl.percent + '%)');
        dist.appendChild(row);
      });
      summaryEl.hidden = false;
    }

    /* ---- dialog mechanics ---- */

    var FOCUSABLE = 'a[href],button:not([disabled]),select,input,[tabindex]:not([tabindex="-1"])';

    function onKeydown(e) {
      if (e.key === 'Escape') {
        /*
         * An open sort menu owns Escape first: this handler and the menu's own
         * both sit on document, this one was attached earlier, so without the
         * guard one Escape would dismiss the menu AND the whole screen. Escape
         * should peel one layer at a time.
         */
        if (modal.querySelector('[data-sn-sort-menu]:not([hidden])')) return;
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = Array.prototype.slice.call(modal.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open() {
      lastFocus = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown);
      if (scrollEl) scrollEl.scrollTop = 0;
      var closeBtn = modal.querySelector('[data-rm-close]');
      if (closeBtn) closeBtn.focus();
      // Deep-linkable, matching the design's own ?reviews=all route. replaceState
      // rather than pushState so Back leaves the page instead of stepping through
      // every open/close.
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('reviews', 'all');
        window.history.replaceState({}, '', url);
      } catch (err) { /* no history API — the dialog still works */ }
    }

    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      try {
        var url = new URL(window.location.href);
        url.searchParams.delete('reviews');
        window.history.replaceState({}, '', url);
      } catch (err) { /* as above */ }
    }

    /* ---- wiring ---- */

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-rm-close]')) { close(); return; }
      var pill = e.target.closest('[data-rm-filter]');
      if (pill && !pill.disabled) {
        filter = pill.dataset.rmFilter;
        Array.prototype.forEach.call(pillsEl.children, function (b) {
          b.setAttribute('aria-pressed', String(b.dataset.rmFilter === filter));
        });
        refresh();
        return;
      }
      if (e.target.closest('[data-rm-more]')) renderPage(false);
    });


    function ensureLoaded() {
      if (loaded) return Promise.resolve();
      return snReviews.load(sid, pid).then(function (data) {
        all = data.reviews;
        buildPills(data.aggregate);
        renderSummary(data.aggregate);
        refresh();
        loaded = true;
      });
    }

    openers.forEach(function (o) {
      o.addEventListener('click', function (e) {
        e.preventDefault();
        // Open immediately so the tap is acknowledged; the list fills a beat
        // later. A failure leaves the dialog open with its empty-state line
        // rather than a blank screen.
        open();
        ensureLoaded().catch(function () {
          if (emptyEl) {
            emptyEl.textContent = 'Reviews could not be loaded right now.';
            emptyEl.hidden = false;
          }
        });
      });
    });

    // Honour ?reviews=all on load, as the design's route does.
    try {
      if (new URLSearchParams(window.location.search).get('reviews') === 'all') {
        open();
        ensureLoaded().catch(function () {});
      }
    } catch (err) { /* ignore */ }
  })();

  /* ---------------- Newsletter (Sneaky Parents Club) ---------------- */
  (function initNewsletter() {
    var form = document.getElementById('sn-po-club-form');
    if (!form) return;
    var success = document.getElementById('sn-po-club-success');
    var errorEl = document.getElementById('sn-po-club-error');

    var input = document.getElementById('sn-po-club-email');

    function setClubError(msg) {
      if (input) input.setAttribute('aria-invalid', msg ? 'true' : 'false');
      if (errorEl) {
        errorEl.textContent = msg || '';
        errorEl.hidden = !msg;
      }
    }

    // The design validates inline: an invalid address gets the berry error
    // border on the field plus its message, instead of only the native bubble.
    form.setAttribute('novalidate', '');
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = input ? input.value.trim() : '';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setClubError('Enter a valid email address.');
        return;
      }
      setClubError('');
      /*
       * signup_source is a profile PROPERTY, the same pattern the popup uses:
       * Klaviyo profiles have no tags (there, tags attach to lists, segments,
       * flows and campaigns), and a property is what a segment filters on.
       * The value rides on the form, like the list id, so each section that
       * renders this form names its own source without owning this file.
       */
      var source = form.getAttribute('data-signup-source');
      subscribeEmail(email, source ? { signup_source: source } : null, form.getAttribute('data-klaviyo-list-id'));
      form.hidden = true;
      if (success) success.hidden = false;
    });
  })();

  /* ---------------- Mystery discount popup ---------------- */
  (function initMystery() {
    var modal = document.getElementById('sn-po-mystery');
    if (!modal) return;
    /*
     * Three steps: an optional question, email capture, confirmation.
     *
     * Suppression is keyed on CONVERSION, not on having seen it once.
     *
     * An earlier version set a sessionStorage flag the first time the popup
     * opened, so after a single view it never appeared again for the rest of the
     * session — which reads exactly like "the popup isn't showing". The
     * reference design tracks this in memory instead, so it reappears on every
     * page load. This follows the design with one deliberate exception: once
     * someone has actually submitted their email, stop asking. Re-prompting a
     * visitor who already gave you their address is pure annoyance, and
     * localStorage is what makes that stick across visits.
     */
    var SUBMITTED_KEY = 'sn_po_mystery_submitted';
    var stepQuestion = document.getElementById('sn-po-mystery-step-question');
    var stepEmail = document.getElementById('sn-po-mystery-step-email');
    var stepDone = document.getElementById('sn-po-mystery-step-done');
    var form = document.getElementById('sn-po-mystery-form');
    var emailEl = document.getElementById('sn-po-mystery-email');
    var errorEl = document.getElementById('sn-po-mystery-error');
    var nextBtn = modal.querySelector('[data-po-mystery-next]');

    function alreadySubmitted() {
      try {
        return localStorage.getItem(SUBMITTED_KEY) === '1';
      } catch (err) {
        // Private mode / storage disabled — fall back to showing it.
        return false;
      }
    }
    function markSubmitted() {
      try { localStorage.setItem(SUBMITTED_KEY, '1'); } catch (err) { /* non-fatal */ }
    }

    function show(step) {
      [stepQuestion, stepEmail, stepDone].forEach(function (el) {
        if (el) el.hidden = el !== step;
      });
    }

    function open() {
      if (alreadySubmitted()) return;
      modal.hidden = false;
    }
    function close() { modal.hidden = true; }

    if (MYSTERY_DELAY >= 0 && !alreadySubmitted()) {
      setTimeout(open, MYSTERY_DELAY);
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-po-mystery-close]') && !modal.hidden) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    /* Step 1 -> 2. The question is optional, so Next never blocks. */
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        show(stepEmail);
        if (emailEl) emailEl.focus();
      });
    }

    /*
     * Every checked answer, as an array of the hyphenated slugs authored in
     * the section markup. The question is multi-select, so this returns 0..n.
     *
     * Queries `modal`, NOT stepQuestion: by the time the email form submits,
     * step 1 is hidden — and inputs inside a hidden element still report
     * :checked, which is what makes reading the answer at submit time work.
     */
    function selectedAnswers() {
      var picked = modal.querySelectorAll('input[name="sn-po-excited"]:checked');
      return Array.prototype.map.call(picked, function (input) { return input.value; });
    }

    function showError(message) {
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      if (emailEl) emailEl.focus();
    }

    /* The design clears the error as soon as the visitor edits the address. */
    if (emailEl) {
      emailEl.addEventListener('input', function () {
        if (errorEl) errorEl.hidden = true;
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var email = emailEl ? emailEl.value.trim() : '';
        /*
         * The form carries `novalidate`, so validity is checked here instead —
         * that is what lets the design's own error copy show rather than the
         * browser's default bubble. One message for both the empty and the
         * malformed case: the design defines a single error string.
         */
        if (!email) return showError('Pop in a valid email address.');
        if (emailEl.validity && !emailEl.validity.valid) return showError('Pop in a valid email address.');
        if (errorEl) errorEl.hidden = true;

        /*
         * Two profile PROPERTIES, not tags: Klaviyo profiles have no tags (there,
         * tags attach to lists, segments, flows and campaigns), and properties
         * are what a segment filters on — which is the whole point of asking.
         *
         * excited_about is an ARRAY of slugs, because the question is
         * multi-select. Klaviyo stores list-valued properties natively and
         * segments on them with `contains`, so a filter written for the old
         * single value keeps matching.
         *
         * The property is omitted entirely when the question was skipped,
         * rather than sending an invented "skipped" value that would then need
         * excluding from every segment. Note the guard below tests .length,
         * not truthiness: [] is truthy, so `if (answers)` would write an empty
         * array onto every skipped profile — exactly what this avoids.
         */
        var answers = selectedAnswers();
        var props = {};
        props[DISCOUNT_PROPERTY] = true;
        props.discount_email_submitted_at = new Date().toISOString();
        props.discount_email_source = DISCOUNT_SOURCE;
        props.signup_source = MYSTERY_SOURCE;
        if (answers.length) props.excited_about = answers;
        if (W2_OFFER) props.signup_offer = W2_OFFER;

        subscribeEmail(email, props, MYSTERY_LIST_ID);
        captureEmail(email);
        /*
         * The welcome offer is a free Carrot Cake bag rather than a discount
         * code, so claiming it has to leave a mark on the ORDER, not just on
         * the Klaviyo profile. claimCarrotCake writes the cart attribute the
         * 3PL picks from and remembers the claim for later visits — see its
         * definition at the top of this file for why it is an attribute.
         * Skipped when the W2 10% offer is live: that offer is a code in the
         * W2 email, not a bag.
         */
        if (!W2_OFFER) claimCarrotCake();

        markSubmitted();
        show(stepDone);
        // Move focus into the new step so a screen reader announces it and the
        // keyboard does not land back at the top of the page behind.
        if (stepDone && stepDone.focus) stepDone.focus();
      });
    }
  })();
})();
